import React, { useState, useEffect, useRef, useCallback } from "react";
import EnquiryFollowUp from "./EnquiryFollowUp";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Swal from "sweetalert2";
import { Pagination } from "@mui/material";
import * as XLSX from "xlsx";

import EnquiryModal from "./enquiryModal";
import TableLoader from "../../Loader/TableLoader";
import {
  useEnquiryList,
  useDeleteEnquiry,
  useUpdateEnquiryStatus,
  useImportEnquiries,
  useInternalUserList,
  useAllocateLeads,
  useLeadDetailsFollowUp,
} from "../hooks/useManageEnquiry";
import { useAddFollowUp } from "../hooks/useFollowUp";
import { useFollowUpOptions, useSelectableFollowUpOptions } from "../hooks/useManageEnquiry";
import { fetchExportEnquiries } from "../services/manageEnquiryService";
import usePermissions from "../hooks/usePermissions";
import { useEmailTemplates, useSendEmailUsingTemplate } from "../hooks/useEmailTemplate";
import { sendLeadEmail, sendLeadEmailWithTemplate } from "../services/emailTemplateService";
import { fetchTemplatePreview } from "../services/placeholderService";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { API_BASE_URL } from "../../config";

import { placeClickToCall } from "../utils/clickToCall";
import { parsePhones, AdditionalPhones, withCountryCode, dialNumber } from "../utils/phones";

import { Button, Input, Tooltip, Select, Dropdown, Popover } from "antd";
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  MailOutlined,
  DownloadOutlined,
  UploadOutlined,
  TagsOutlined,
  UserSwitchOutlined,
  UserOutlined,
  ArrowRightOutlined,
  PhoneOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  EditOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        cursor: "pointer",
        minHeight: "42px",
        background: "white",
        transition: "all 0.2s",
        width: "100%",
        fontSize: "14px",
        color: value ? "#262626" : "#bfbfbf",
      }}
    >
      <CalendarOutlined
        style={{ color: "#667eea", fontSize: "14px", flexShrink: 0 }}
      />
      <span style={{ flex: 1 }}>{value || placeholder}</span>
    </div>
  ),
);

const EMPTY_FILTER = {
  search: "",
  start_date: "",
  end_date: "",
  status: "",
  is_assigned: "",
  assign_to: "",
};

// Color map keyed by follow-up option_value (matches item.status)
const FOLLOW_UP_STYLE = {
  "0":  { bg:"#eff6ff", color:"#2563eb", border:"#bfdbfe" },
  "1":  { bg:"#fff1f2", color:"#e11d48", border:"#fecdd3" },
  "2":  { bg:"#f5f5f5", color:"#6b7280", border:"#d1d5db" },
  "3":  { bg:"#f0fdf4", color:"#16a34a", border:"#bbf7d0" },
  "4":  { bg:"#fffbeb", color:"#d97706", border:"#fde68a" },
  "5":  { bg:"#fef2f2", color:"#dc2626", border:"#fecaca" },
  "6":  { bg:"#f5f3ff", color:"#7c3aed", border:"#ddd6fe" },
  "7":  { bg:"#ecfdf5", color:"#059669", border:"#6ee7b7" },
  "10": { bg:"#fef2f2", color:"#dc2626", border:"#fecaca" },
};

const statusColor = (s) => {
  const m = {
    "No Response": "#9ca3af",
    Interested: "#16a34a",
    "Not Interested": "#6b7280",
    "Meeting Scheduled": "#0284c7",
    Converted: "#15803d",
    "Follow Up": "#667eea",
    "Hot Lead": "#ef4444",
    "Mark for Future": "#f59e0b",
    Negative: "#dc2626",
    pending: "#f59e0b",
    completed: "#16a34a",
    cancelled: "#ef4444",
    rescheduled: "#0891b2",
  };
  return m[s] || "#667eea";
};

const MODE_NONE = null;
const MODE_DELETE = "delete";
const MODE_EXPORT = "export";
const MODE_ALLOCATE = "allocate";

// ─── EMAIL COMPOSE HELPERS ────────────────────────────────────────────────────
const parseAttachmentsME = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try {
    const p = JSON.parse(val);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};

const statusColorME = (s) => {
  const m = {
    "No Response": "#9ca3af", Interested: "#16a34a", "Not Interested": "#6b7280",
    "Meeting Scheduled": "#0284c7", Converted: "#15803d",
    "Follow Up": "#667eea", "Hot Lead": "#ef4444",
    "Mark for Future": "#f59e0b", Negative: "#dc2626",
  };
  return m[s] || "#667eea";
};

const FollowUpStatusSelectME = ({ value, onChange, followUpOptionsList = [], style = {} }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef();
  const selected = followUpOptionsList.find((o) => o.option_value === value);
  const color = statusColorME(selected?.label || "");

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", ...style }}>
      <button type="button" onClick={() => setOpen((p) => !p)}
        style={{ width: "100%", padding: "9px 36px 9px 12px", border: `1.5px solid ${color}`, borderRadius: "9px", fontSize: "13px", fontWeight: 700, color, background: `${color}10`, cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: "8px", boxSizing: "border-box", transition: "all 0.2s", boxShadow: open ? `0 0 0 3px ${color}20` : "none", position: "relative" }}>
        <span style={{ flex: 1 }}>{selected?.label || "— Select Status —"}</span>
        <span style={{ position: "absolute", right: "11px", top: "50%", transform: `translateY(-50%) rotate(${open ? "180deg" : "0deg"})`, transition: "transform 0.2s", color, pointerEvents: "none", fontSize: "10px" }}>▼</span>
      </button>
      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 9999, background: "white", border: "1.5px solid #e8ecff", borderRadius: "12px", boxShadow: "0 8px 32px rgba(102,126,234,0.18)", overflow: "hidden" }}>
          <div style={{ maxHeight: "220px", overflowY: "auto", padding: "6px" }}>
            {followUpOptionsList.map((opt) => {
              const c = statusColorME(opt.label);
              const isActive = opt.option_value === value;
              return (
                <button key={opt.option_value} type="button"
                  onClick={() => { onChange(opt.option_value); setOpen(false); }}
                  style={{ width: "100%", padding: "8px 12px", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: isActive ? 700 : 500, color: isActive ? "white" : c, background: isActive ? c : "transparent", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", gap: "9px", transition: "all 0.15s", marginBottom: "2px" }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = `${c}15`; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  {opt.label}
                  {isActive && <span style={{ marginLeft: "auto", fontSize: "12px" }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const EmailFileChipME = ({ file, onRemove }) => {
  const name = file instanceof File ? file.name : file.name || "Attachment";
  const rawSize = file instanceof File ? file.size : file.size ? Number(file.size) : 0;
  const sizeLabel = rawSize
    ? rawSize > 1024 * 1024
      ? `${(rawSize / (1024 * 1024)).toFixed(1)} MB`
      : `${(rawSize / 1024).toFixed(0)} KB`
    : "";
  const fileUrl = file instanceof File ? URL.createObjectURL(file) : file.url;
  const isImage = file instanceof File
    ? file.type?.startsWith("image/")
    : /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/i.test(name);
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "5px 10px", background: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: "8px", fontSize: "11px", color: "#667eea", fontWeight: 600, maxWidth: "220px" }}>
      {isImage && fileUrl
        ? <img src={fileUrl} alt={name} style={{ width: "24px", height: "24px", borderRadius: "4px", objectFit: "cover", border: "1px solid #c7d2fe" }} />
        : <span style={{ fontSize: "13px" }}>📄</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", fontSize: "11px", fontWeight: 600, color: "#374151" }}>{name}</span>
        {sizeLabel && <span style={{ fontSize: "10px", color: "#9ca3af" }}>{sizeLabel}</span>}
      </div>
      {file.url && (
        <a href={file.url} target="_blank" rel="noreferrer" style={{ color: "#667eea", textDecoration: "none", fontSize: "13px", fontWeight: 700 }}>↓</a>
      )}
      {onRemove && (
        <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", display: "flex", alignItems: "center", padding: "0 0 0 2px", fontSize: "15px", lineHeight: 1 }}>×</button>
      )}
    </div>
  );
};

const EmailPreviewModalME = ({ subject, body, onClose }) => (
  <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,0.55)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
    <div style={{ background: "white", borderRadius: "18px", width: "100%", maxWidth: "700px", boxShadow: "0 28px 64px rgba(0,0,0,0.22)", overflow: "hidden", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "linear-gradient(135deg,#3b82f6,#2563eb)", padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ color: "white", fontWeight: 700, fontSize: "14px" }}>Template Preview</div>
          <div style={{ color: "rgba(255,255,255,.7)", fontSize: "11px", marginTop: "2px" }}>{subject}</div>
        </div>
        <button onClick={onClose} style={{ width: "28px", height: "28px", borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "white", cursor: "pointer", fontSize: "16px" }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div dangerouslySetInnerHTML={{ __html: body }} style={{ minHeight: "200px", padding: "16px" }} />
      </div>
    </div>
  </div>
);

// ── Kanban column definitions (shared by kanban view + View All filter) ─────
const KANBAN_COLUMNS = [
  { key: "new",         label: "New",       color: "#6366f1", border: "#c7d2fe", bg: "#eef2ff" },
  { key: "contacted",   label: "Contacted", color: "#0891b2", border: "#bae6fd", bg: "#e0f2fe" },
  { key: "future",      label: "Future",    color: "#d97706", border: "#fde68a", bg: "#fffbeb" },
  { key: "closed_won",  label: "Done",      color: "#16a34a", border: "#86efac", bg: "#dcfce7" },
  { key: "closed_lost", label: "Negative",  color: "#dc2626", border: "#fca5a5", bg: "#fee2e2" },
];

const ManageEnquiry = () => {
  const { canCreate, canEdit, canDelete, importExport } = usePermissions("/cms/manageenquiry");
  const [pageApi, setPageApi] = useState(1);
  const [filterForm, setFilterForm] = useState(EMPTY_FILTER);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // shared checkbox state
  const [checkboxMode, setCheckboxMode] = useState(MODE_NONE);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [selectAll, setSelectAll] = useState(false);

  const [importActive, setImportActive] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [statusId, setStatusId] = useState(0);
  const [statusCode, setStatusCode] = useState(0);
  const [modalIsOpen2, setIsOpen2] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [followUpEnquiry, setFollowUpEnquiry] = useState(null);
  const [callLogModal, setCallLogModal] = useState(null);
  const [callOutcome, setCallOutcome] = useState("Connected");
  const [callNote, setCallNote] = useState("");
  const [callDurationME, setCallDurationME] = useState("");
  const [callFuStatusME, setCallFuStatusME] = useState("");
  const [callNextDateME, setCallNextDateME] = useState("");
  const [callNextTimeME, setCallNextTimeME] = useState("");

  /* Returns the helper's promise: it resolves false when the dialer refused the
     call (number not registered, missing, network error), and the caller uses
     that to keep the call-log form closed — a log for a call that never happened
     is worse than no log. */
  const handleClickToCall = (clientNo, clientName) =>
    placeClickToCall(clientNo, clientName);

  // ── View mode (list / kanban) ──────────────────────────────────────────────
  const [viewMode, setViewMode] = useState("kanban");
  // ── CHANGE 1: kanban filter state ──
  const [kanbanFilter, setKanbanFilter] = useState(null);

  // ── Email compose modal ───────────────────────────────────────────────────
  const [emailModal, setEmailModal] = useState(null);
  const [emailToME, setEmailToME] = useState("");
  const [emailCcListME, setEmailCcListME] = useState([]);
  const [emailCcInputME, setEmailCcInputME] = useState("");
  const [emailCcErrorME, setEmailCcErrorME] = useState("");
  const [emailBccListME, setEmailBccListME] = useState([]);
  const [emailBccInputME, setEmailBccInputME] = useState("");
  const [emailBccErrorME, setEmailBccErrorME] = useState("");
  const [showEmailCcME, setShowEmailCcME] = useState(false);
  const [showEmailBccME, setShowEmailBccME] = useState(false);
  const [emailTemplateME, setEmailTemplateME] = useState("");
  const [emailSubjectME, setEmailSubjectME] = useState("");
  const [emailBodyME, setEmailBodyME] = useState("");
  const [emailBodyModeME, setEmailBodyModeME] = useState("compose");
  const emailEditorRefME = useRef(null);
  const emailExecCmdME = useCallback((cmd, val) => {
    emailEditorRefME.current?.focus();
    document.execCommand(cmd, false, val);
  }, []);
  useEffect(() => {
    if (emailBodyModeME === "compose" && emailEditorRefME.current) {
      emailEditorRefME.current.innerHTML = emailBodyME;
    }
  }, [emailBodyModeME]); // eslint-disable-line react-hooks/exhaustive-deps
  const [emailFilesME, setEmailFilesME] = useState([]);
  const [templateAttachmentsME, setTemplateAttachmentsME] = useState([]);
  const [emailNextDateME, setEmailNextDateME] = useState("");
  const [emailNextTimeME, setEmailNextTimeME] = useState("");
  const [emailRemarksME, setEmailRemarksME] = useState("");
  const [emailFuStatusME, setEmailFuStatusME] = useState("");
  const [showPreviewME, setShowPreviewME] = useState(false);

  // ── Allocate state ────────────────────────────────────────────────────────
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [allocateLoading, setAllocateLoading] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  const searchDebounceRef = useRef(null);
  const emailFileRefME = useRef();
  const composeScrollRefME = useRef();
  const scrollContainerRef = useRef(null);
  const throttleRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  /* The board needs every lead to bucket correctly, so it pulls bigger pages —
     1000 leads becomes 5 requests instead of 100. The table stays at 10/page. */
  const listQueryFilter = React.useMemo(
    () => (viewMode === "kanban" ? { ...appliedFilter, limit: 200 } : appliedFilter),
    [appliedFilter, viewMode],
  );

  const {
    data: listData,
    isLoading,
    isFetching,
  } = useEnquiryList(pageApi, listQueryFilter);
  const { data: internalUsers = [] } = useInternalUserList();
  const deleteEnquiry = useDeleteEnquiry();
  const updateStatus = useUpdateEnquiryStatus();
  const importMutation = useImportEnquiries();
  const allocateLeads = useAllocateLeads();
  const addFollowUpME = useAddFollowUp();
  const queryClient = useQueryClient();
  const { data: leadDetails, isLoading: isLeadLoading } =
    useLeadDetailsFollowUp(selectedLeadId);
  const { data: followUpOptions = [] } = useFollowUpOptions();       // labels
  const { data: selectableFollowUpOptions = [] } = useSelectableFollowUpOptions(); // pickable
  // Lead screen → only lead templates
  const { data: emailTemplates = [], isLoading: templatesLoading } = useEmailTemplates("lead");

  const navigate = useNavigate();
  const sendTemplateMutationME = useSendEmailUsingTemplate();
  const [isSendingEmailME, setIsSendingEmailME] = React.useState(false);

  // Per-lead unread email count badge
  const [leadUnreadMap, setLeadUnreadMap] = React.useState({});
  const fetchLeadEmailUnread = React.useCallback(async () => {
    try {
      const internalUserId = Number(localStorage.getItem("trade_user_id"));
      const res = await axios.get(`${API_BASE_URL}/lead-inbox/unread-count`, {
        params: { internal_user_id: internalUserId },
        headers: { Authorization: `Bearer ${localStorage.getItem("cms_token")}` },
      });
      const byLead = res.data?.by_lead || [];
      const map = {};
      byLead.forEach((item) => {
        if (item.lead_id && item.count > 0) {
          map[String(item.lead_id)] = item.count;
        }
      });
      setLeadUnreadMap(map);
    } catch {}
  }, []);

  const followUpStatusMap = React.useMemo(() => {
    const map = {};
    followUpOptions.forEach((opt) => {
      map[String(opt.option_value)] = opt.label;
    });
    return map;
  }, [followUpOptions]);

  const selectedTemplateME = React.useMemo(() => {
    if (!emailTemplateME) return null;
    return emailTemplates.find((t) => String(t.id) === String(emailTemplateME)) || null;
  }, [emailTemplateME, emailTemplates]);

  const listPageData = listData?.data ?? [];
  const totalUser = listData?.total ?? 0;
  const totalPages = listData?.totalPages ?? 1;

  // ── Accumulated list data for list-view infinite scroll ──────────────────
  const [listAccum, setListAccum] = useState([]);
  useEffect(() => {
    if (!listData?.data) return;
    const pages = listData.totalPages ?? 1;
    if (pageApi === 1) {
      setListAccum(listData.data);
      setHasMore(pages > 1);
    } else {
      setListAccum((prev) => {
        /* Merge by id, don't just append the unseen ones. The old version kept
           the first copy of a row forever, so a refetch after allocating a lead
           never refreshed assign_to_name — it only showed up after a reload. */
        const byId = new Map(prev.map((i) => [i.id, i]));
        listData.data.forEach((i) => byId.set(i.id, i));
        return Array.from(byId.values());
      });
      setHasMore(pageApi < pages);
    }
    setIsLoadingMore(false);
    fetchLeadEmailUnread();
  }, [listData, fetchLeadEmailUnread]);

  /* The per-lead follow-up fetch that used to power New-vs-Contacted is gone:
     it fired one request per lead (1000 leads = 1000 requests) and only covered
     the current page, so leads beyond it fell into "New" by accident.
     `status` on the row answers the same question for free. */

  // ── CHANGE 2: classifyLead, handleKanbanViewAll, filteredListData, kanbanFilterLabel ──
  /**
   * Everything needed is on the lead row — no per-lead follow-up fetch.
   *
   * `status` holds a lead_follow_up_status option_value and is NULL until
   * somebody logs a follow-up, so "has any value" == contacted.
   *   null → new · 4 → future · 7 → done · 10 → negative · anything else → contacted
   * A future follow-up date also means Future, even if the lead was contacted.
   */
  /* Placement comes from the API's `kanban_column` — one source of truth for
     every screen. This board's own keys are older (closed_won / closed_lost), so
     the API's names are mapped onto them; `status` stays a card label only. */
  const classifyLead = React.useCallback((item) => {
    const col = String(item?.kanban_column ?? "").trim().toLowerCase();
    const KEY_BY_API = {
      new: "new",
      contacted: "contacted",
      future: "future",
      done: "closed_won",
      negative: "closed_lost",
    };
    return KEY_BY_API[col] || "new";
  }, []);

  /* Future / Done / Negative already have their own dedicated screens, so
     "View All" sends the user there instead of showing a filtered copy of the
     same list here. New and Contacted have no such page — they stay in-page. */
  const KANBAN_VIEW_ALL_ROUTES = {
    future: "/cms/manageenquiry/future",
    closed_won: "/cms/manageenquiry/done",
    closed_lost: "/cms/manageenquiry/negative-enquiry",
  };

  const handleKanbanViewAll = (columnKey) => {
    const route = KANBAN_VIEW_ALL_ROUTES[columnKey];
    if (route) {
      navigate(route);
      return;
    }
    setKanbanFilter(columnKey);
    setViewMode("list");
  };

  const filteredListData = kanbanFilter
    ? listAccum.filter((item) => classifyLead(item) === kanbanFilter)
    : listAccum;

  const kanbanFilterLabel = kanbanFilter
    ? KANBAN_COLUMNS.find((c) => c.key === kanbanFilter)?.label || kanbanFilter
    : null;

  // ── CHANGE 9: display count ──
  const displayCount = kanbanFilter ? filteredListData.length : totalUser;

  useEffect(() => {
    if (!followUpEnquiry) return;
    window.history.pushState({ followUpOpen: true }, "");
    const onPop = () => setFollowUpEnquiry(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [followUpEnquiry]);

  useEffect(() => {
    if (!isFetching) setIsFilterLoading(false);
  }, [isFetching]);

  useEffect(() => {
    if (leadDetails) {
      setFollowUpEnquiry({
        id: leadDetails.id,
        name: leadDetails.name,
        email: leadDetails.email,
        phone: leadDetails.phone,
        additional_phone: leadDetails.additional_phone || "",
        company: leadDetails.company,
        address: leadDetails.address,
        message: leadDetails.message,
        status:
          leadDetails.is_positive === 1
            ? "Positive"
            : leadDetails.is_positive === 2
              ? "Negative"
              : "Neutral",
        assignToId: leadDetails.assign_to || null,
        assignedToName: leadDetails.assign_to_name || "Unassigned",
        stage: leadDetails.stage_name,
        followUps: leadDetails.follow_ups || [],
        // Attribution is carried through to Add Customer when the lead
        // converts, so it must survive this mapping.
        utm_source:    leadDetails.utm_source    || "",
        utm_medium:    leadDetails.utm_medium    || "",
        utm_campaign:  leadDetails.utm_campaign  || "",
      });
    }
  }, [leadDetails]);
  

  const formatIsPositive = (val) =>
    ({ 0: "Neutral", 1: "Positive", 2: "Negative" })[val] ?? "Pending";

  const resetSelection = () => {
    setCheckboxMode(MODE_NONE);
    setSelectedIds([]);
    setSelectAll(false);
    setSelectedAdmin(null);
  };

  /* Dropdown filters apply straight away — same feel as the search box, no
     "Apply Filters" click needed. Only the one changed key is merged, so the
     date range and anything already applied stay put. */
  const handleInstantFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterForm((p) => ({ ...p, [name]: value }));
    setListAccum([]);
    setHasMore(true);
    setIsLoadingMore(false);
    setPageApi(1);
    setAppliedFilter((p) => ({ ...p, [name]: value }));
  };
  const handleSearchChange = (e) => {
    const v = e.target.value;
    setFilterForm((p) => ({ ...p, search: v }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setListAccum([]);
      setHasMore(true);
      setIsLoadingMore(false);
      setPageApi(1);
      setAppliedFilter((p) => ({ ...p, search: v }));
    }, 500);
  };

  const applyFilter = () => {
    setListAccum([]);
    setHasMore(true);
    setIsLoadingMore(false);
    setPageApi(1);
    setIsFilterLoading(true);
    setAppliedFilter({
      ...filterForm,
      start_date: startDate
        ? moment(startDate).utc().add(1, "d").format("YYYY-MM-DD")
        : "",
      end_date: endDate
        ? moment(endDate).utc().add(1, "d").format("YYYY-MM-DD")
        : "",
    });
  };
  const clearFilter = () => {
    setFilterForm(EMPTY_FILTER);
    setStartDate("");
    setEndDate("");
    setAppliedFilter(EMPTY_FILTER);
    setListAccum([]);
    setHasMore(true);
    setIsLoadingMore(false);
    setPageApi(1);
    setKanbanFilter(null);
  };

  const handleViewFollowUp = (id) => {
    setSelectedLeadId(id);
  };

  /* A converted lead is a customer: its story continues on the customer profile.
     `customer_id` and `subscription` now ride along on every lead row, so no
     extra request is needed here. */
  const openLeadCard = (item) => {
    if (String(item?.status ?? "") === "7" && item?.customer_id) {
      navigate(`/cms/manageUserData/user-profile/${item.customer_id}`);
      return;
    }
    handleViewFollowUp(item.id);
  };

  // ── Email compose helpers ──────────────────────────────────────────────────
  const isValidEmailME = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleAddCcEmailME = () => {
    const v = emailCcInputME.trim();
    if (!v) return;
    if (!isValidEmailME(v)) { setEmailCcErrorME("Invalid email address"); return; }
    if (emailCcListME.includes(v)) { setEmailCcErrorME("Email already added"); return; }
    setEmailCcListME((p) => [...p, v]);
    setEmailCcInputME("");
    setEmailCcErrorME("");
  };

  const handleAddBccEmailME = () => {
    const v = emailBccInputME.trim();
    if (!v) return;
    if (!isValidEmailME(v)) { setEmailBccErrorME("Invalid email address"); return; }
    if (emailBccListME.includes(v)) { setEmailBccErrorME("Email already added"); return; }
    setEmailBccListME((p) => [...p, v]);
    setEmailBccInputME("");
    setEmailBccErrorME("");
  };

  const handleTemplateChangeME = async (templateId) => {
    setEmailTemplateME(templateId);
    if (!templateId) {
      setEmailSubjectME("");
      setEmailBodyME("");
      setTemplateAttachmentsME([]);
      return;
    }
    try {
      const preview = await fetchTemplatePreview(templateId, emailModal?.id);
      setEmailSubjectME(preview.subject || "");
      setEmailBodyME(preview.body || "");
      setTemplateAttachmentsME(parseAttachmentsME(preview.attachments));
    } catch {
      const tpl = emailTemplates.find((t) => String(t.id) === String(templateId));
      if (tpl) {
        setEmailSubjectME(tpl.subject || "");
        setEmailBodyME(tpl.body || "");
        setTemplateAttachmentsME(parseAttachmentsME(tpl.attachments));
      }
    }
  };

  // Auto-scroll compose area to bottom when new files are attached so chips are visible
  useEffect(() => {
    if (emailModal && emailFilesME.length > 0 && composeScrollRefME.current) {
      composeScrollRefME.current.scrollTop = composeScrollRefME.current.scrollHeight;
    }
  }, [emailFilesME.length, emailModal]);

  const resetEmailFormME = () => {
    setEmailToME("");
    setEmailCcListME([]);
    setEmailCcInputME("");
    setEmailCcErrorME("");
    setEmailBccListME([]);
    setEmailBccInputME("");
    setEmailBccErrorME("");
    setShowEmailCcME(false);
    setShowEmailBccME(false);
    setEmailTemplateME("");
    setEmailSubjectME("");
    setEmailBodyME("");
    setEmailBodyModeME("compose");
    if (emailEditorRefME.current) emailEditorRefME.current.innerHTML = "";
    setEmailFilesME([]);
    setTemplateAttachmentsME([]);
    setEmailNextDateME("");
    setEmailNextTimeME("");
    setEmailRemarksME("");
    setEmailFuStatusME("");
    setShowPreviewME(false);
  };

  const handleSendEmail = async () => {
    if (!emailBodyME.replace(/<[^>]*>/g, "").trim() && !selectedTemplateME) {
      toast.error("Message body is required");
      return;
    }
    const nowISO = moment().format("YYYY-MM-DD HH:mm:ss");
    const createdBy = Number(localStorage.getItem("cms_user_id")) || 1;
    const buildNextFU = (date, time) => {
      if (!date) return {};
      return { next_followup_date: date, ...(time ? { next_followup_time: time } : {}) };
    };
    const fromEmail = (() => {
      try { return JSON.parse(localStorage.getItem("cms_user") || "{}").email || ""; }
      catch { return ""; }
    })();
    const internalUserId = Number(localStorage.getItem("cms_user_id")) || 1;

    setIsSendingEmailME(true);
    try {
      if (selectedTemplateME) {
        const result = await sendLeadEmailWithTemplate({
          template_id: selectedTemplateME.id,
          from: fromEmail,
          to: emailToME,
          lead_id: emailModal.id,
          internal_user_id: internalUserId,
          ...(emailCcListME.length > 0 ? { cc: emailCcListME } : {}),
          ...(emailBccListME.length > 0 ? { bcc: emailBccListME } : {}),
          ...(emailFilesME.length > 0 ? { files: emailFilesME } : {}),
          ...(templateAttachmentsME.length > 0 ? { existing_attachments: JSON.stringify(templateAttachmentsME) } : {}),
        });
        toast.success(result?.message || "Email sent successfully!");
      } else {
        const result = await sendLeadEmail({
          from: fromEmail,
          to: emailToME,
          subject: emailSubjectME,
          body: emailBodyME,
          lead_id: emailModal.id,
          internal_user_id: internalUserId,
          ...(emailCcListME.length > 0 ? { cc: emailCcListME } : {}),
          ...(emailBccListME.length > 0 ? { bcc: emailBccListME } : {}),
          ...(emailFilesME.length > 0 ? { files: emailFilesME } : {}),
        });
        toast.success(result?.message || "Email sent successfully!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to send email");
      setIsSendingEmailME(false);
      return;
    }

    // ── Log follow-up (unchanged logic) ──
    const stripHtml = (html) => {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    };

    const addFUPayload = selectedTemplateME
      ? {
          lead_id: emailModal.id,
          followup_type: "email",
          followup_date: nowISO,
          enquiry_status: emailFuStatusME,
          direction: "outbound",
          delivery_status: "sent",
          recipient: emailToME,
          subject: emailSubjectME,
          content: emailBodyME
            ? stripHtml(emailBodyME.replace(/<br\s*\/?>/gi, "\n").replace(/<\/p>/gi, "\n"))
                .replace(/\n{3,}/g, "\n\n").trim()
            : `Sent via template: ${selectedTemplateME.template_name.replace(/_/g, " ")}`,
          template_used: selectedTemplateME.template_name,
          remarks: emailRemarksME || null,
          created_by: createdBy,
          ...(emailCcListME.length > 0 ? { cc: emailCcListME } : {}),
          ...buildNextFU(emailNextDateME, emailNextTimeME),
          attachments: emailFilesME.length > 0 ? emailFilesME : undefined,
          ...(templateAttachmentsME.length > 0 ? { existing_attachments: JSON.stringify(templateAttachmentsME) } : {}),
        }
      : {
          lead_id: emailModal.id,
          followup_type: "email",
          followup_date: nowISO,
          enquiry_status: emailFuStatusME,
          direction: "outbound",
          delivery_status: "sent",
          recipient: emailToME,
          subject: emailSubjectME,
          content: emailBodyME,
          template_used: null,
          remarks: emailRemarksME || null,
          created_by: createdBy,
          ...(emailCcListME.length > 0 ? { cc: emailCcListME } : {}),
          ...buildNextFU(emailNextDateME, emailNextTimeME),
          attachments: emailFilesME.length > 0 ? emailFilesME : undefined,
        };

    addFollowUpME.mutate(addFUPayload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "lead-followups" && String(q.queryKey[1]) === String(emailModal.id) });
        setEmailModal(null);
        resetEmailFormME();
      },
      onError: () => toast.error("Failed to log email follow-up"),
      onSettled: () => setIsSendingEmailME(false),
    });
  };

  // ── CHANGE 8: handleCheckAll uses filteredListData ──
  const handleCheckAll = () => {
    const n = !selectAll;
    setSelectAll(n);
    setSelectedIds(n ? filteredListData.map((i) => i.id) : []);
  };
  const handleCheckboxChange = (item) =>
    setSelectedIds((p) =>
      p.includes(item.id) ? p.filter((id) => id !== item.id) : [...p, item.id],
    );

  const handleDeleteSingleRow = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((r) => {
      if (r.isConfirmed)
        deleteEnquiry.mutate(id, {
          onSuccess: () =>
            Swal.fire("Deleted!", "Enquiry has been deleted.", "success"),
          onError: () => Swal.fire("Error", "Failed to delete.", "error"),
        });
    });
  };
  const handleConfirmDelete = () => {
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: "error",
        title: "No leads selected",
        text: "Please select at least one lead to delete.",
      });
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: `Delete ${selectedIds.length} selected lead(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    }).then((r) => {
      if (r.isConfirmed)
        deleteEnquiry.mutate(selectedIds, {
          onSuccess: () => {
            // listAccum merges pages by id, so a refetch alone never removes a
            // deleted lead from the Kanban — drop the ids here.
            const deleted = new Set(selectedIds.map(String));
            setListAccum((prev) => prev.filter((i) => !deleted.has(String(i.id))));
            Swal.fire("Deleted!", "Selected leads deleted.", "success");
            resetSelection();
          },
          onError: () => Swal.fire("Error", "Failed to delete.", "error"),
        });
      else Swal.fire("Cancelled", "Your records are safe.", "info");
    });
  };

  const handleExportButtonClick = async () => {
    if (checkboxMode !== MODE_EXPORT) {
      setCheckboxMode(MODE_EXPORT);
      setSelectedIds([]);
      setSelectAll(false);
      // Kanban cards have no checkboxes, so drop into List view to tick leads —
      // same as the allocate flow does.
      setViewMode("list");
      return;
    }
    if (selectedIds.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No leads selected",
        text: "Please select at least one lead to export.",
        confirmButtonColor: "#06b6d4",
      });
      return;
    }
    try {
      setExportLoading(true);
      const raw = await fetchExportEnquiries(appliedFilter);
      const selected = raw.filter((item) => selectedIds.includes(item.id));
      if (selected.length === 0) {
        Swal.fire({ icon: "error", title: "No data", text: "Could not find data for selected leads." });
        return;
      }
      const rows = selected.map((item) => ({
        Name: item.name || "", Email: item.email || "", Phone: item.phone || "",
        "UTM Source": item.utm_source || "", "UTM Medium": item.utm_medium || "",
        "UTM Campaign": item.utm_campaign || "", "System IP": item.system_ip || "",
        "Source URL": item.source_url || "", Message: item.message || "",
        Date: item.created_date || moment(item.created_at).format("DD-MM-YYYY"),
        Status: formatIsPositive(item.is_positive),
        "Assigned To": item.assign_to_name || "Unassigned",
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [{ wch: 22 },{ wch: 28 },{ wch: 16 },{ wch: 16 },{ wch: 16 },{ wch: 22 },{ wch: 16 },{ wch: 32 },{ wch: 40 },{ wch: 14 },{ wch: 12 },{ wch: 18 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Leads");
      const filename = `leads-export-${moment().format("YYYY-MM-DD")}.xlsx`;
      XLSX.writeFile(wb, filename);
      toast.success(`Exported ${selected.length} lead(s) to ${filename}`);
      resetSelection();
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenAllocateModal = () => setShowAllocateModal(true);

  const handleAdminSelected = (adminObj) => {
    setSelectedAdmin(adminObj);
    setShowAllocateModal(false);
    setCheckboxMode(MODE_ALLOCATE);
    setSelectedIds([]);
    setSelectAll(false);
    setViewMode("list");
  };

  const handleConfirmAllocate = () => {
    if (!selectedAdmin) { toast.error("Please select an admin first."); return; }
    if (selectedIds.length === 0) {
      Swal.fire({ icon: "warning", title: "No leads selected", text: "Please tick at least one lead to allocate.", confirmButtonColor: "#8b5cf6" });
      return;
    }
    const limit = selectedAdmin.lead_limit ?? 0;
    const alreadyHas = selectedAdmin.total_assigned_leads ?? 0;
    const wouldHave = alreadyHas + selectedIds.length;
    if (limit > 0 && wouldHave > limit) {
      const canTake = Math.max(0, limit - alreadyHas);
      Swal.fire({
        icon: "error", title: "Lead Limit Exceeded",
        html: `<p><strong>${selectedAdmin.name}</strong> has a lead limit of <strong>${limit}</strong>.</p><p>Currently assigned: <strong>${alreadyHas}</strong> &nbsp;·&nbsp; You're trying to add: <strong>${selectedIds.length}</strong></p><p>Can still accept: <strong style="color:${canTake > 0 ? "#16a34a" : "#dc2626"}">${canTake}</strong> more lead(s).</p>`,
        confirmButtonColor: "#8b5cf6",
      });
      return;
    }
    Swal.fire({
      title: "Confirm Allocation",
      html: `Assign <strong>${selectedIds.length}</strong> lead(s) to <strong>${selectedAdmin.name}</strong>?`,
      icon: "question", showCancelButton: true, confirmButtonText: "Yes, Allocate", confirmButtonColor: "#8b5cf6",
    }).then((r) => {
      if (!r.isConfirmed) return;
      setAllocateLoading(true);
      allocateLeads.mutate(
        { adminId: selectedAdmin.id, leadIds: selectedIds },
        {
          onSuccess: () => {
            Swal.fire({ icon: "success", title: "Allocated!", text: `${selectedIds.length} lead(s) assigned to ${selectedAdmin.name}.`, confirmButtonColor: "#8b5cf6" });
            resetSelection();
            /* Reload from page 1: allocated leads may sit on pages that are no
               longer active queries, so invalidation alone would leave them
               showing the old (blank) assignee until a manual refresh. */
            setListAccum([]);
            setHasMore(true);
            setIsLoadingMore(false);
            setPageApi(1);
          },
          onError: (err) => Swal.fire("Error", err?.message || "Failed to allocate leads. Please try again.", "error"),
          onSettled: () => setAllocateLoading(false),
        },
      );
    });
  };

  const handleNegative = (id) =>
    Swal.fire({
      title: "Mark as Negative?", input: "textarea", inputLabel: "Remark",
      inputPlaceholder: "Enter your remark...", icon: "warning", showCancelButton: true,
      confirmButtonText: "Yes, Mark Negative", confirmButtonColor: "#dc2626",
    }).then((r) => {
      if (r.isConfirmed) updateStatus.mutate({ id, status: 2, message: r.value || "" });
    });

  const handleFileChange = (e) => { setFile(e.target.files[0]); setFileError(""); };
  const handleImport = () => {
    if (!file) { setFileError("File is required"); return; }
    importMutation.mutate(file, {
      onSuccess: () => {
        setImportActive(false);
        setFile(null);
        /* Imported leads land at the top and shift every page, so rebuild the
           accumulated list from page 1 instead of merging into the old one. */
        setListAccum([]);
        setHasMore(true);
        setIsLoadingMore(false);
        setPageApi(1);
      },
    });
  };

  const activeFiltersCount = [filterForm.search, filterForm.status, filterForm.is_assigned, filterForm.assign_to, startDate, endDate].filter(Boolean).length;
  const labelStyle = { fontWeight: 600, color: "#374151", fontSize: "13px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "5px" };
  const statusBadge = (val) => {
    if (val == 1) return { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", label: "● Positive" };
    if (val == 2) return { bg: "#fef2f2", color: "#dc2626", border: "#fecaca", label: "● Negative" };
    if (val == 0) return { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe", label: "● Neutral" };
    return { bg: "#fffbeb", color: "#d97706", border: "#fde68a", label: "● Pending" };
  };

  const showCheckboxes = checkboxMode !== MODE_NONE;
  const inDeleteMode = checkboxMode === MODE_DELETE;
  const inExportMode = checkboxMode === MODE_EXPORT;
  const inAllocateMode = checkboxMode === MODE_ALLOCATE;
  const chkColor = inExportMode ? "#0284c7" : inAllocateMode ? "#8b5cf6" : "#dc2626";

  // ── Infinite scroll ───────────────────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return;
    setIsLoadingMore(true);
    setPageApi((v) => v + 1);
  }, [isLoadingMore, hasMore, isLoading]);

  const handleScroll = useCallback(
    (e) => {
      if (throttleRef.current || !hasMore || isLoadingMore) return;
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 200) {
        throttleRef.current = true;
        loadMore();
        setTimeout(() => { throttleRef.current = false; }, 300);
      }
    },
    [loadMore, hasMore, isLoadingMore],
  );

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || viewMode !== "list") return;
    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [viewMode, handleScroll]);

  /* List and Kanban request different page sizes, so restart pagination when the
     user SWITCHES view. It must skip the initial render: on mount the query can
     resolve from cache in the same commit, and since this effect is declared
     after the one that fills listAccum it would wipe the rows straight away —
     that is what left every column empty when coming back to the page. */
  const prevViewModeRef = useRef(viewMode);
  useEffect(() => {
    if (prevViewModeRef.current === viewMode) return;
    prevViewModeRef.current = viewMode;
    /* Deliberately NOT clearing listAccum here. Both effects run in the same
       commit and this one is declared later, so a clear would wipe the rows the
       accumulate effect just wrote — that is what emptied every column on
       Kanban → List → Kanban. Resetting the page is enough: the accumulate
       effect replaces the whole array whenever pageApi === 1. */
    setHasMore(true);
    setIsLoadingMore(false);
    setPageApi(1);
  }, [viewMode]);

  /* Kanban has no single scroll container to hang infinite scroll on (each column
     scrolls independently), and a board built from one page shows wrong counts —
     26 total records but only 10 cards. So while the board is open, keep pulling
     the next page until everything is in, capped so a huge pipeline can't spam
     the API. */
  const KANBAN_MAX_LEADS = 500;
  useEffect(() => {
    if (viewMode !== "kanban") return;
    if (!hasMore || isLoadingMore || isLoading) return;
    if (listAccum.length >= KANBAN_MAX_LEADS) return;
    loadMore();
  }, [viewMode, hasMore, isLoadingMore, isLoading, listAccum.length, loadMore]);

  const LoadingMoreRow = () => (
    <div style={{ textAlign: "center", padding: "16px", color: "#9ca3af" }}>
      <div className="spinner-border spinner-border-sm" style={{ color: "#667eea", marginRight: 8 }} role="status" />
      <span style={{ fontSize: 13 }}>Loading more...</span>
    </div>
  );

  return (
    <>
      <style>{`
        .custom-datepicker-wrapper{width:100%}.custom-datepicker-wrapper .react-datepicker-wrapper{width:100%}.custom-datepicker-wrapper .react-datepicker__input-container{width:100%}.custom-datepicker-wrapper .react-datepicker-popper{z-index:9999!important}.custom-datepicker-wrapper .react-datepicker{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;border:1px solid #e8ecff;border-radius:12px;box-shadow:0 8px 24px rgba(102,126,234,.15);overflow:hidden;font-size:13px}.custom-datepicker-wrapper .react-datepicker__month-container{width:280px}.custom-datepicker-wrapper .react-datepicker__header{background:linear-gradient(135deg,#667eea 0%,#764ba2 100%);border-bottom:none;padding:10px 8px 6px;border-radius:0}.custom-datepicker-wrapper .react-datepicker__current-month{color:#fff;font-weight:700;font-size:14px;margin-bottom:4px}.custom-datepicker-wrapper .react-datepicker__day-names{display:flex;justify-content:space-around}.custom-datepicker-wrapper .react-datepicker__day-name{color:rgba(255,255,255,.85);font-weight:600;font-size:11px;width:32px;line-height:32px;margin:1px;text-align:center}.custom-datepicker-wrapper .react-datepicker__month{margin:6px}.custom-datepicker-wrapper .react-datepicker__week{display:flex;justify-content:space-around}.custom-datepicker-wrapper .react-datepicker__day{width:32px;line-height:32px;margin:1px;border-radius:6px;font-size:13px;color:#374151;transition:all .15s;text-align:center}.custom-datepicker-wrapper .react-datepicker__day:hover{background:#f0f4ff;color:#667eea}.custom-datepicker-wrapper .react-datepicker__day--selected,.custom-datepicker-wrapper .react-datepicker__day--keyboard-selected{background:linear-gradient(135deg,#667eea 0%,#764ba2 100%)!important;color:#fff!important;font-weight:600}.custom-datepicker-wrapper .react-datepicker__day--today{font-weight:700;color:#667eea}.custom-datepicker-wrapper .react-datepicker__day--disabled{color:#d1d5db!important}.custom-datepicker-wrapper .react-datepicker__day--outside-month{color:#d1d5db}.custom-datepicker-wrapper .react-datepicker__navigation{top:10px}.custom-datepicker-wrapper .react-datepicker__navigation-icon::before{border-color:rgba(255,255,255,.8);border-width:2px 2px 0 0;height:7px;width:7px}.custom-datepicker-wrapper .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before{border-color:#fff}.custom-datepicker-wrapper .react-datepicker__triangle{display:none}
        @keyframes badgePop{0%{transform:scale(1)}50%{transform:scale(1.2)}100%{transform:scale(1)}}
        .sel-badge{animation:badgePop .25s ease}
        @keyframes meCallModalIn{from{opacity:0;transform:scale(.92) translateY(14px)}to{opacity:1;transform:scale(1) translateY(0)}}
        @keyframes meAllocModalIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        .me-cl-btn{transition:transform .18s,box-shadow .18s;cursor:pointer}.me-cl-btn:hover{transform:translateY(-1px)}
        @keyframes slideInRight{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
        .alloc-admin-card{border:2px solid #e5e7eb;border-radius:12px;padding:14px 16px;cursor:pointer;transition:all .18s;background:white}
        .alloc-admin-card:hover{border-color:#8b5cf6;background:#faf5ff;transform:translateY(-1px);box-shadow:0 4px 14px rgba(139,92,246,.15)}
        .alloc-admin-card.selected{border-color:#8b5cf6;background:#faf5ff;box-shadow:0 0 0 3px rgba(139,92,246,.15)}
        .alloc-admin-card.over-limit{border-color:#fca5a5;background:#fef2f2;cursor:not-allowed;opacity:.7}
        .alloc-admin-card.over-limit:hover{transform:none;box-shadow:none}
        .lead-name-link{cursor:pointer;transition:color 0.15s,opacity 0.15s}
        .lead-name-link:hover{opacity:0.8}
        .lead-name-link:hover .lead-avatar{transform:scale(1.08)}
        .lead-avatar{transition:transform 0.15s}
      `}</style>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* PAGE HEADER */}
            <div style={{ background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", borderRadius: "16px", padding: "28px 32px", marginBottom: "28px", boxShadow: "0 8px 32px rgba(102,126,234,.28)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <div style={{ width: "56px", height: "56px", background: "rgba(255,255,255,.22)", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "26px", color: "white" }}><MailOutlined /></div>
                <div>
                  <h4 style={{ color: "white", margin: 0, fontSize: "24px", fontWeight: 700 }}>Manage Lead</h4>
                  <p style={{ color: "rgba(255,255,255,.85)", margin: 0, fontSize: "14px" }}>View and manage all lead records</p>
                </div>
              </div>
              <div style={{ background: "rgba(255,255,255,.18)", padding: "10px 18px", borderRadius: "10px", display: "flex", gap: "6px", alignItems: "center", fontSize: "13px", color: "white" }}>
                <a href="javascript:void(0);" style={{ color: "white", textDecoration: "none" }}>CMS</a>
                <span style={{ color: "rgba(255,255,255,.5)" }}>›</span>
                <span style={{ fontWeight: 600 }}>Manage Lead</span>
              </div>
            </div>

            {/* FILTER CARD */}
            <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,.07)", border: "1px solid #f0f0f0", marginBottom: "24px", overflow: "visible" }}>
              <div style={{ background: "linear-gradient(135deg,#f8f9ff 0%,#f0f4ff 100%)", borderBottom: "2px solid #e8ecff", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "16px" }}><FilterOutlined /></div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "16px", color: "#1f2937" }}>Advanced Filters</div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>Narrow down your lead search</div>
                  </div>
                </div>
                {activeFiltersCount > 0 && (<span style={{ background: "linear-gradient(135deg,#667eea,#764ba2)", color: "white", borderRadius: "20px", padding: "4px 14px", fontSize: "13px", fontWeight: 600 }}>{activeFiltersCount} active</span>)}
              </div>
              <div style={{ padding: "24px" }}>
                <div className="row g-3">
                  <div className="col-md-4">
                    <div style={labelStyle}><SearchOutlined style={{ color: "#667eea" }} /> Name, Email or Mobile</div>
                    <Input size="large" placeholder="Search by name, email or mobile..." name="search" value={filterForm.search} onChange={handleSearchChange} prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />} style={{ borderRadius: "8px" }} allowClear />
                  </div>
                  <div className="col-md-4">
                    <div style={labelStyle}><UserSwitchOutlined style={{ color: "#667eea" }} /> Assignment Status</div>
                    <select className="form-select" name="is_assigned" value={filterForm.is_assigned} onChange={handleInstantFilterChange} style={{ borderRadius: "8px", border: "1px solid #e0e0e0", minHeight: "42px", fontSize: "14px", color: "#262626", padding: "8px 12px" }}>
                      <option value="">All</option>
                      <option value="1">Assigned</option>
                      <option value="0">Unassigned</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <div style={labelStyle}><UserOutlined style={{ color: "#667eea" }} /> Internal User</div>
                    <select className="form-select" name="assign_to" value={filterForm.assign_to} onChange={handleInstantFilterChange} style={{ borderRadius: "8px", border: "1px solid #e0e0e0", minHeight: "42px", fontSize: "14px", color: "#262626", padding: "8px 12px" }}>
                      <option value="">All Internal Users</option>
                      {internalUsers.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <div style={labelStyle}><CalendarOutlined style={{ color: "#667eea" }} /> Start Date</div>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker selected={startDate} onChange={(d) => setStartDate(d)} selectsStart startDate={startDate} endDate={endDate} dateFormat="dd MMM yyyy" placeholderText="Select start date" isClearable={!!startDate} customInput={<CustomDateInput placeholder="Select start date" />} popperPlacement="bottom-start" popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "offset", options: { offset: [0, 4] } }]} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div style={labelStyle}><CalendarOutlined style={{ color: "#667eea" }} /> End Date</div>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker selected={endDate} onChange={(d) => setEndDate(d)} selectsEnd startDate={startDate} endDate={endDate} minDate={startDate} dateFormat="dd MMM yyyy" placeholderText="Select end date" isClearable={!!endDate} customInput={<CustomDateInput placeholder="Select end date" />} popperPlacement="bottom-start" popperModifiers={[{ name: "preventOverflow", options: { boundary: "viewport" } }, { name: "offset", options: { offset: [0, 4] } }]} />
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "20px", paddingTop: "20px", borderTop: "1px solid #f0f0f0" }}>
                  <Button size="large" icon={<ClearOutlined />} onClick={clearFilter} style={{ borderRadius: "10px", minWidth: "130px", fontWeight: 600, borderColor: "#667eea", color: "#667eea", height: "44px" }}>Clear Filters</Button>
                  <Button type="primary" size="large" icon={<FilterOutlined />} onClick={applyFilter} loading={isFilterLoading} style={{ borderRadius: "10px", minWidth: "130px", fontWeight: 600, height: "44px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", border: "none", boxShadow: "0 4px 12px rgba(102,126,234,.4)" }}>Apply Filters</Button>
                </div>
              </div>
            </div>

            <EnquiryModal token={localStorage.getItem("cms_token")} modalIsOpen2={modalIsOpen2} setIsOpen2={setIsOpen2} id={statusId} setStatusId={setStatusId} statusCode={statusCode} setStatusCode={setStatusCode} setModalStatus={setModalStatus} modalStatus={modalStatus} />

            {/* TABLE CARD */}
            <div style={{ background: "white", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,.07)", border: "1px solid #f0f0f0", overflow: "hidden" }}>
              {/* ── Action bar ── */}
              <div style={{ padding: "20px 24px", borderBottom: "2px solid #f5f5f5", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                  <div style={{ width: "38px", height: "38px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "16px" }}><MailOutlined /></div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "17px", color: "#1f2937" }}>Lead List</div>
                    {/* CHANGE 9: use displayCount */}
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>{displayCount} total {displayCount === 1 ? "record" : "records"}</div>
                  </div>
                  {/* CHANGE 9: use displayCount */}
                  <span style={{ background: "#f0f4ff", color: "#667eea", borderRadius: "20px", padding: "3px 12px", fontSize: "13px", fontWeight: 700 }}>{displayCount}</span>
                  {showCheckboxes && selectedIds.length > 0 && (
                    <span key={selectedIds.length} className="sel-badge" style={{ background: inExportMode ? "linear-gradient(135deg,#06b6d4,#0284c7)" : inAllocateMode ? "linear-gradient(135deg,#8b5cf6,#7c3aed)" : "linear-gradient(135deg,#ef4444,#dc2626)", color: "white", borderRadius: "20px", padding: "3px 12px", fontSize: "13px", fontWeight: 700 }}>{selectedIds.length} selected</span>
                  )}
                  {inAllocateMode && selectedAdmin && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#faf5ff", border: "1.5px solid #c4b5fd", borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, color: "#7c3aed" }}>
                      <TeamOutlined /> {selectedAdmin.name}
                      <span style={{ background: "#ede9fe", borderRadius: "10px", padding: "1px 7px", fontSize: "11px" }}>Assigned: {selectedAdmin.total_assigned_leads ?? 0} / Limit: {selectedAdmin.lead_limit}</span>
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
                  {importExport && (
                    <button onClick={() => setImportActive((p) => !p)} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: importActive ? "linear-gradient(135deg,#667eea 0%,#764ba2 100%)" : "white", color: importActive ? "white" : "#667eea", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "2px solid #667eea", cursor: "pointer", transition: "all 0.2s" }}><UploadOutlined /> Import</button>
                  )}
                  {importExport && (
                    <>
                      {inExportMode && (<button onClick={resetSelection} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "white", color: "#6b7280", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "2px solid #e0e0e0", cursor: "pointer" }}>✕ Cancel</button>)}
                      <button onClick={handleExportButtonClick} disabled={exportLoading} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "linear-gradient(135deg,#06b6d4 0%,#0284c7 100%)", color: "white", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "none", cursor: "pointer", boxShadow: inExportMode ? "0 4px 14px rgba(6,182,212,.45)" : "0 4px 12px rgba(6,182,212,.3)", outline: inExportMode ? "2px solid #0284c7" : "none", outlineOffset: "2px", transition: "all 0.2s" }}>
                        {exportLoading ? (<span className="spinner-border spinner-border-sm text-white" />) : (<><DownloadOutlined /> {inExportMode ? "Confirm Export" : "Export"}</>)}
                      </button>
                    </>
                  )}
                  {inAllocateMode && (<button onClick={resetSelection} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "white", color: "#6b7280", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "2px solid #e0e0e0", cursor: "pointer" }}>✕ Cancel</button>)}
                  <button onClick={inAllocateMode ? handleConfirmAllocate : handleOpenAllocateModal} disabled={allocateLeads.isPending || allocateLoading} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "linear-gradient(135deg,#8b5cf6 0%,#7c3aed 100%)", color: "white", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "none", cursor: "pointer", boxShadow: inAllocateMode ? "0 4px 14px rgba(139,92,246,.45)" : "0 4px 12px rgba(139,92,246,.3)", outline: inAllocateMode ? "2px solid #7c3aed" : "none", outlineOffset: "2px", transition: "all 0.2s" }}>
                    {allocateLoading ? (<span className="spinner-border spinner-border-sm text-white" />) : (<><TeamOutlined /> {inAllocateMode ? "Confirm Allocate" : "Allocate"}</>)}
                  </button>
                  {canDelete && (
                    <>
                      {inDeleteMode && (<button onClick={resetSelection} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "white", color: "#6b7280", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "2px solid #e0e0e0", cursor: "pointer" }}>✕ Cancel</button>)}
                      <button onClick={() => { if (checkboxMode !== MODE_DELETE) { setCheckboxMode(MODE_DELETE); setSelectedIds([]); setSelectAll(false); setSelectedAdmin(null); setViewMode("list"); return; } handleConfirmDelete(); }} disabled={deleteEnquiry.isPending} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "linear-gradient(135deg,#ef4444 0%,#dc2626 100%)", color: "white", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", border: "none", cursor: "pointer", boxShadow: "0 4px 12px rgba(239,68,68,.3)", outline: inDeleteMode ? "2px solid #dc2626" : "none", outlineOffset: "2px", transition: "all 0.2s" }}>
                        <DeleteOutlined /> {inDeleteMode ? "Confirm Delete" : "Delete Selected"}
                      </button>
                    </>
                  )}

                  {/* ── View Toggle — CHANGE 3: kanban clears filter ── */}
                  <div style={{ display: "flex", border: "1.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
                    <button onClick={() => { setViewMode("kanban"); setKanbanFilter(null); }} style={{ padding: "7px 14px", border: "none", background: viewMode === "kanban" ? "linear-gradient(135deg,#667eea,#764ba2)" : "white", color: viewMode === "kanban" ? "white" : "#667eea", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s" }}><AppstoreOutlined /> Kanban</button>
                    <button onClick={() => setViewMode("list")} style={{ padding: "7px 14px", border: "none", borderRight: "1px solid #e2e8f0", background: viewMode === "list" ? "linear-gradient(135deg,#667eea,#764ba2)" : "white", color: viewMode === "list" ? "white" : "#667eea", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s" }}><UnorderedListOutlined /> List</button>
                  </div>

                  {canCreate && (
                    <Link to="/cms/manageenquiry/add-enquiry" style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "linear-gradient(135deg,#52c41a,#389e0d)", color: "white", borderRadius: "10px", padding: "8px 18px", fontWeight: 600, fontSize: "14px", textDecoration: "none", boxShadow: "0 4px 12px rgba(82,196,26,.3)" }}><PlusOutlined /> Add Lead</Link>
                  )}
                </div>
              </div>

              {/* ── Helper instruction bar ── */}
              {showCheckboxes && (
                <div style={{ padding: "10px 24px", background: inExportMode ? "linear-gradient(135deg,#ecfeff,#e0f2fe)" : inAllocateMode ? "linear-gradient(135deg,#f5f3ff,#ede9fe)" : "linear-gradient(135deg,#fef2f2,#fee2e2)", borderBottom: `2px solid ${inExportMode ? "#bae6fd" : inAllocateMode ? "#c4b5fd" : "#fecaca"}`, display: "flex", alignItems: "center", gap: "10px", fontSize: "13px", color: inExportMode ? "#0284c7" : inAllocateMode ? "#7c3aed" : "#dc2626" }}>
                  {inExportMode ? (<><DownloadOutlined /> <strong>Export mode:</strong>&nbsp;Tick leads to export, then click <strong>Confirm Export</strong>.</>) : inAllocateMode ? (<><TeamOutlined /> <strong>Allocate mode:</strong>&nbsp;Tick leads to assign to <strong>{selectedAdmin?.name}</strong>, then click <strong>Confirm Allocate</strong>.</>) : (<><DeleteOutlined /> <strong>Delete mode:</strong>&nbsp;Tick leads to delete, then click <strong>Confirm Delete</strong>.</>)}
                  {/* CHANGE 7: use filteredListData.length */}
                  <span style={{ marginLeft: "auto", fontWeight: 700 }}>{selectedIds.length} / {filteredListData.length} selected on this page</span>
                </div>
              )}

              {/* Import section */}
              {importActive && (
                <div style={{ padding: "16px 24px", background: "#f8f9ff", borderBottom: "1px solid #e8ecff" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px", maxWidth: "600px", marginLeft: "auto" }}>
                    <input className={`form-control ${fileError ? "is-invalid" : ""}`} type="file" onChange={handleFileChange} style={{ borderRadius: "8px" }} />
                    {fileError && (<div className="invalid-feedback">{fileError}</div>)}
                    <button style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "linear-gradient(135deg,#667eea 0%,#764ba2 100%)", color: "white", borderRadius: "8px", padding: "8px 16px", fontWeight: 600, fontSize: "14px", border: "none", cursor: "pointer", whiteSpace: "nowrap" }} disabled={importMutation.isPending} onClick={handleImport}>
                      {importMutation.isPending ? (<span className="spinner-border spinner-border-sm text-white" />) : (<><UploadOutlined /> Import</>)}
                    </button>
                    <a href="/assets/images/import_enquiry_format_trademark.xlsx" download style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "white", color: "#667eea", borderRadius: "8px", padding: "8px 16px", fontWeight: 600, fontSize: "14px", border: "2px solid #667eea", textDecoration: "none", whiteSpace: "nowrap" }}><DownloadOutlined /> Format</a>
                  </div>
                  <div style={{ maxWidth: "600px", marginLeft: "auto", marginTop: "8px", fontSize: "11px", color: "#6b7280" }}>
                    Required columns: <b>name</b>, <b>phone</b> (without country code), <b>country_code</b> (e.g. +91), <b>email</b>.
                  </div>
                </div>
              )}

              {viewMode === "list" ? (
              <>
              {/* CHANGE 5: Kanban filter banner */}
              {kanbanFilter && (
                <div style={{ padding: "10px 24px", background: "#f0f4ff", borderBottom: "1px solid #e8ecff", display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, color: "#667eea", fontWeight: 600 }}>Showing: {kanbanFilterLabel} leads</span>
                  <button onClick={() => setKanbanFilter(null)} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", fontSize: 17, lineHeight: 1, padding: "0 4px" }}>×</button>
                </div>
              )}
              {/* Table */}
              <div ref={scrollContainerRef} style={{ maxHeight: "calc(100vh - 340px)", overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", minWidth: 1200, borderCollapse: "collapse" }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                    <tr style={{ background: "linear-gradient(135deg,#f8f9ff 0%,#f0f4ff 100%)", borderBottom: "2px solid #e8ecff" }}>
                      {showCheckboxes && (<th style={{ padding: "14px 16px", textAlign: "center" }}><input className="form-check-input" type="checkbox" checked={selectAll} onChange={handleCheckAll} style={{ accentColor: chkColor }} /></th>)}
                      {["Name","Email","Mobile","Assigned To","Message","Date","Status","UTM Sources","Sources","Actions"].map((h) => (
                        <th key={h} style={{ padding: "14px 16px", textAlign: "center", fontSize: "12px", fontWeight: 700, color: "#667eea", textTransform: "uppercase", letterSpacing: "0.6px", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableLoader />
                    ) : filteredListData.length > 0 ? (
                      /* CHANGE 6: use filteredListData */
                      filteredListData.map((item, idx) => {
                        const isSel = selectedIds.includes(item.id);
                        const rowBg = isSel ? (inExportMode ? "#ecfeff" : inAllocateMode ? "#f5f3ff" : "#fef2f2") : idx % 2 === 0 ? "white" : "#fafbff";
                        return (
                          <tr key={item.id} style={{ borderBottom: "1px solid #f5f5f5", transition: "background 0.15s", background: rowBg }} onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = "#f0f4ff"; }} onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = rowBg; }}>
                            {showCheckboxes && (<td style={{ padding: "14px 16px", textAlign: "center" }}><input type="checkbox" checked={isSel} onChange={() => handleCheckboxChange(item)} style={{ accentColor: chkColor, width: "15px", height: "15px" }} /></td>)}
                            <td style={{ padding: "14px 16px", textAlign: "left" }}>
                              <Tooltip title="View Follow Up" placement="right">
                                <div className="lead-name-link" onClick={() => handleViewFollowUp(item.id)} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                  <div style={{ position: "relative", flexShrink: 0 }}>
                                    <div className="lead-avatar" style={{ width: "34px", height: "34px", borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "13px", fontWeight: 700 }}>{item.name?.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"}</div>
                                    {leadUnreadMap[String(item.id)] > 0 && (
                                      <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, borderRadius: 8, background: "#e74856", border: "1.5px solid #fff", color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>{leadUnreadMap[String(item.id)]}</span>
                                    )}
                                  </div>
                                  <span style={{ fontWeight: 600, color: "#667eea", fontSize: "14px" }}>{item?.name}</span>
                                </div>
                              </Tooltip>
                            </td>
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563", minWidth: "180px", whiteSpace: "nowrap" }}>{item?.email ? (<span style={{ background: "#eff6ff", color: "#2563eb", borderRadius: "4px", padding: "2px 8px", fontSize: "12px", display: "inline-block", whiteSpace: "nowrap" }}>{item.email}</span>) : (<span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>)}</td>
                            <td style={{ padding: "14px 16px", fontSize: "13px", whiteSpace: "nowrap" }}>
                              {item?.phone ? (
                                <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                    <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />
                                    <span style={{ fontWeight: 700, color: "#111827", fontSize: "13px" }}>{withCountryCode(item.country_code, item.phone)}</span>
                                    {parsePhones(item.additional_phone).length > 0 ? (
                                      <Dropdown
                                        trigger={["click"]}
                                        menu={{
                                          items: [
                                            { key: "primary", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}><PhoneOutlined style={{ fontSize: "11px" }} /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{withCountryCode(item.country_code, item.phone)}</div></div></div>) },
                                            ...parsePhones(item.additional_phone).map((alt, i, arr) => ({ key: `alt-${alt}`, label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}><PhoneOutlined style={{ fontSize: "11px" }} /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>{arr.length > 1 ? `Alternate ${i + 1}` : "Alternate"}</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{alt}</div></div></div>) })),
                                          ],
                                          onClick: ({ key }) => {
                                            const num = key === "primary" ? item.phone : key.replace(/^alt-/, "");
                                            handleClickToCall(dialNumber(item.country_code, num), item.name).then((placed) => {
                                              if (!placed) return;   // dialer refused — nothing to log
                                              setCallOutcome("Connected"); setCallNote(""); setCallDurationME(""); setCallFuStatusME(""); setCallNextDateME(""); setCallNextTimeME("");
                                              setCallLogModal({ phone: num, name: item.name, id: item.id });
                                            });
                                          },
                                        }}
                                      >
                                        <button style={{ width: "18px", height: "18px", borderRadius: "5px", background: "#dcfce7", border: "none", color: "#16a34a", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, fontSize: "11px", flexShrink: 0 }}><PhoneOutlined /></button>
                                      </Dropdown>
                                    ) : (
                                      <Tooltip title="Log a Call">
                                        <button onClick={() => { handleClickToCall(dialNumber(item.country_code, item.phone), item.name).then((placed) => { if (!placed) return; setCallOutcome("Connected"); setCallNote(""); setCallDurationME(""); setCallFuStatusME(""); setCallNextDateME(""); setCallNextTimeME(""); setCallLogModal({ phone: item.phone, name: item.name, id: item.id }); }); }} style={{ width: "18px", height: "18px", borderRadius: "5px", background: "#dcfce7", border: "none", color: "#16a34a", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0, fontSize: "11px", flexShrink: 0 }}><PhoneOutlined /></button>
                                      </Tooltip>
                                    )}
                                  </div>
                                  <AdditionalPhones value={item.additional_phone} dot max={2} itemStyle={{ color: "#374151", fontSize: "12px" }} />
                                </div>
                              ) : (<span style={{ color: "#9ca3af", fontSize: "12px" }}>N/A</span>)}
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center", whiteSpace: "nowrap" }}>{item?.assign_to_name ? (<div><span style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, whiteSpace: "nowrap" }}>{item.assign_to_name}</span>{item?.assign_to_role && (<small style={{ display: "block", color: "#9ca3af", marginTop: "3px", fontSize: "11px" }}>{item.assign_to_role}</small>)}</div>) : (<span style={{ background: "#f5f5f5", color: "#9ca3af", borderRadius: "20px", padding: "3px 12px", fontSize: "12px", fontWeight: 600, fontStyle: "italic" }}>Unassigned</span>)}</td>
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563", whiteSpace: "nowrap" }}>
                              {item.message ? (
                                <Popover trigger="hover" title="Message" overlayStyle={{ maxWidth: 360 }} content={<div style={{ maxWidth: 320, maxHeight: 280, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.6 }}>{item.message}</div>}>
                                  <span style={{ cursor: "pointer", color: "#374151" }}>{item.message.length > 50 ? item.message.slice(0, 50) + "…" : item.message}</span>
                                </Popover>
                              ) : "N/A"}
                            </td>
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563", whiteSpace: "nowrap", textAlign: "center" }}>{item.created_date || moment(item.created_at).format("DD MMM YYYY")}</td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              {(() => {
                                if (item.status === null || item.status === undefined) {
                                  return (<span style={{ background:"#f1f5f9", color:"#64748b", border:"1px solid #cbd5e1", borderRadius:"20px", padding:"3px 12px", fontSize:"12px", fontWeight:600, whiteSpace:"nowrap" }}>● Pending</span>);
                                }
                                const statusKey = String(item.status);
                                const style = FOLLOW_UP_STYLE[statusKey] || { bg:"#fffbeb", color:"#d97706", border:"#fde68a" };
                                const label = followUpStatusMap[statusKey] || statusBadge(item.is_positive).label;
                                return (<span style={{ background:style.bg, color:style.color, border:`1px solid ${style.border}`, borderRadius:"20px", padding:"3px 12px", fontSize:"12px", fontWeight:600, whiteSpace:"nowrap" }}>● {label}</span>);
                              })()}
                            </td>
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563" }}>
                              <div style={{ marginBottom: "2px" }}><span style={{ color: "#9ca3af", fontSize: "12px" }}>Source: </span>{item?.utm_source || "N/A"}</div>
                              <div style={{ marginBottom: "2px" }}><span style={{ color: "#9ca3af", fontSize: "12px" }}>Medium: </span>{item?.utm_medium || "N/A"}</div>
                              <div><span style={{ color: "#9ca3af", fontSize: "12px" }}>Campaign: </span>{item?.utm_campaign ? item.utm_campaign.substring(0, 10) + "..." : "N/A"}</div>
                            </td>
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563" }}>
                              <div style={{ marginBottom: "2px" }}><span style={{ color: "#9ca3af", fontSize: "12px" }}>IP: </span>{item.system_ip ? (<a href={`https://ip-api.com/#${item.system_ip}`} target="_blank" rel="noreferrer" style={{ color: "#667eea" }}>{item.system_ip}</a>) : "N/A"}</div>
                              <div><span style={{ color: "#9ca3af", fontSize: "12px" }}>URL: </span>{item.source_url ? (<a href={item.source_url} target="_blank" rel="noreferrer"><span style={{ background: "#f0f4ff", color: "#667eea", borderRadius: "4px", padding: "1px 6px", fontSize: "12px" }}>Click Here</span></a>) : "N/A"}</div>
                            </td>
                            <td style={{ padding: "14px 16px", textAlign: "center" }}>
                              <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                                <Tooltip title="View Follow Up"><button onClick={() => handleViewFollowUp(item.id)} style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "white", borderRadius: "8px", border: "none", cursor: "pointer", transition: "all 0.2s", padding: "5px 10px", boxShadow: "0 2px 8px rgba(102,126,234,.4)" }}><EyeOutlined style={{ fontSize: "12px", flexShrink: 0 }} /><ArrowRightOutlined style={{ fontSize: "11px", flexShrink: 0 }} /></button></Tooltip>
                                {canEdit && (<Tooltip title="Edit Lead"><Link to={`/cms/manageEnquiry/edit-enquiry/${item.id}`}><button style={{ width: "32px", height: "32px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#eff6ff", color: "#2563eb", borderRadius: "8px", border: "1px solid #bfdbfe", cursor: "pointer", fontSize: "13px" }}><EditOutlined /></button></Link></Tooltip>)}
                                {canDelete && (<Tooltip title="Delete"><button onClick={() => handleDeleteSingleRow(item.id)} disabled={deleteEnquiry.isPending} style={{ width: "32px", height: "32px", display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#fef2f2", color: "#dc2626", borderRadius: "8px", border: "1px solid #fecaca", cursor: "pointer", fontSize: "13px" }}><DeleteOutlined /></button></Tooltip>)}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={showCheckboxes ? 11 : 10} style={{ padding: "60px 20px", textAlign: "center" }}>
                          <div style={{ color: "#9ca3af" }}>
                            <MailOutlined style={{ fontSize: "48px", marginBottom: "12px", display: "block" }} />
                            <div style={{ fontSize: "16px", fontWeight: 600, color: "#6b7280" }}>No Data Found</div>
                            <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "4px" }}>Try adjusting your filters</div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {isLoadingMore && <LoadingMoreRow />}
                {!hasMore && filteredListData.length > 0 && !isLoading && (
                  <div style={{ textAlign: "center", padding: "14px", fontSize: 12, color: "#9ca3af" }}>
                    All {kanbanFilter ? filteredListData.length : totalUser} records loaded
                  </div>
                )}
              </div>
              </>
              ) : (
              /* ── KANBAN VIEW — CHANGE 10: use KANBAN_COLUMNS + classifyLead ── */
              <div style={{ padding: "20px 24px", overflowX: "auto" }}>
                {/* The board auto-pulls the remaining pages; say so, otherwise the
                    counts look wrong while they are still arriving. */}
                {!isLoading && (isLoadingMore || hasMore) && listAccum.length < KANBAN_MAX_LEADS && (
                  <div style={{ marginBottom: 12, fontSize: 12, color: "#6b7280", display: "flex", alignItems: "center", gap: 8 }}>
                    <span className="spinner-border spinner-border-sm" style={{ width: 13, height: 13, color: "#6366f1" }} />
                    Loading all leads… ({listAccum.length} of {totalUser})
                  </div>
                )}
                {!isLoading && listAccum.length >= KANBAN_MAX_LEADS && hasMore && (
                  <div style={{ marginBottom: 12, fontSize: 12, color: "#d97706" }}>
                    Showing the first {KANBAN_MAX_LEADS} leads — use filters or List view to see the rest.
                  </div>
                )}
                {isLoading ? (
                  <div style={{ textAlign: "center", padding: 48, color: "#9ca3af", fontSize: 15 }}>Loading...</div>
                ) : (
                  <div style={{ display: "flex", gap: 16, alignItems: "flex-start", minWidth: "fit-content" }}>
                    {(() => {
                      const buckets = {};
                      KANBAN_COLUMNS.forEach((c) => (buckets[c.key] = []));
                      // listAccum, not listPageData: the board must classify every
                      // loaded lead, not just the current API page of 10.
                      listAccum.forEach((item) => {
                        const col = classifyLead(item);
                        if (buckets[col]) buckets[col].push(item);
                      });
                      const cols = KANBAN_COLUMNS.map((c) => ({ ...c, items: buckets[c.key] || [] }));

                      const renderCol = (col) => (
                        <div key={col.key} style={{ flex: "0 0 280px", background: "#f8fafc", borderRadius: 14, border: `1px solid ${col.border}`, overflow: "hidden" }}>
                          <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `2px solid ${col.border}`, background: "white" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
                              <span style={{ background: col.bg, color: col.color, border: `1px solid ${col.border}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700 }}>{col.label}</span>
                              <span style={{ background: col.color, color: "white", borderRadius: "50%", width: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>{col.items.length}</span>
                              {col.items.length > 0 && (
                                <button
                                  onClick={() => handleKanbanViewAll(col.key)}
                                  style={{ background: `${col.color}18`, border: `1px solid ${col.color}40`, color: col.color, cursor: "pointer", fontSize: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 6, transition: "all 0.15s", marginLeft: "auto" }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = `${col.color}28`; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = `${col.color}18`; }}
                                >
                                  View All <UnorderedListOutlined />
                                </button>
                              )}
                            </div>
                            {canCreate && (
                              <Tooltip title="Add lead">
                                <Link to="/cms/manageenquiry/add-enquiry" style={{ color: col.color, fontSize: 18, lineHeight: 1, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, borderRadius: 6, background: col.bg, marginLeft: 6 }}>+</Link>
                              </Tooltip>
                            )}
                          </div>
                          <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 10, maxHeight: 620, overflowY: "auto" }}>
                            {col.items.length === 0 ? (
                              <div style={{ textAlign: "center", padding: "32px 16px", color: "#d1d5db", fontSize: 13 }}>No leads</div>
                            ) : (
                              col.items.map((item) => (
                                <div key={item.id} style={{ background: "white", borderRadius: 12, padding: "14px 14px 10px", border: "1px solid #f0f0f0", boxShadow: "0 1px 4px rgba(0,0,0,0.05)", transition: "box-shadow 0.15s" }} onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,0.1)")} onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.05)")}>
                                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                                    <div onClick={() => openLeadCard(item)} style={{ display: "flex", alignItems: "center", gap: 8, flex: 1, cursor: "pointer", minWidth: 0 }}>
                                      <div style={{ position: "relative", flexShrink: 0 }}>
                                        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#667eea,#764ba2)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 12, fontWeight: 700 }}>{item.name?.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?"}</div>
                                        {leadUnreadMap[String(item.id)] > 0 && (
                                          <span style={{ position: "absolute", top: -4, right: -4, minWidth: 16, height: 16, borderRadius: 8, background: "#e74856", border: "1.5px solid #fff", color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px" }}>{leadUnreadMap[String(item.id)]}</span>
                                        )}
                                      </div>
                                      <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name || "—"}</div>
                                        {item.company && (<div style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.company}</div>)}
                                      </div>
                                    </div>
                                    <Tooltip title="View / Follow-up"><button onClick={() => handleViewFollowUp(item.id)} style={{ width: 26, height: 26, border: "none", background: "#f3f4f6", borderRadius: 6, cursor: "pointer", color: "#6b7280", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, flexShrink: 0 }}><EyeOutlined /></button></Tooltip>
                                  </div>
                                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                                    {item.message && (<div style={{ fontSize: 12, color: "#374151", background: "#f9fafb", border: "1px solid #f0f0f0", borderRadius: 8, padding: "6px 10px", wordBreak: "break-word", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{item.message}</div>)}
                                    {item.email && (<div style={{ display: "flex", alignItems: "center", gap: 5, overflow: "hidden" }}><MailOutlined style={{ color: "#9ca3af", fontSize: 11, flexShrink: 0 }} /><span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.email}</span></div>)}
                                    {item.phone && (
                                      <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                          <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#16a34a", flexShrink: 0 }} />
                                          <span style={{ fontWeight: 700, color: "#111827", fontSize: "12px" }}>{withCountryCode(item.country_code, item.phone)}</span>
                                        </div>
                                        <AdditionalPhones value={item.additional_phone} dot max={2} itemStyle={{ color: "#374151", fontSize: "11px" }} />
                                      </div>
                                    )}
                                    {/* Assigned To — same info the list view shows */}
                                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                      <UserOutlined style={{ color: "#9ca3af", fontSize: 11, flexShrink: 0 }} />
                                      {item.assign_to_name ? (
                                        <span style={{ color: "#374151", fontWeight: 600, fontSize: 11, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                          {item.assign_to_name}
                                        </span>
                                      ) : (
                                        <span style={{ color: "#d97706", fontStyle: "italic", fontSize: 11 }}>Unassigned</span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Follow-up status is a LABEL on the card — the
                                      column comes from kanban_column. */}
                                  {item.status !== null && item.status !== undefined && followUpStatusMap[String(item.status)] && (
                                    <div style={{ paddingTop: 8 }}>
                                      {(() => {
                                        const key = String(item.status);
                                        const st = FOLLOW_UP_STYLE[key] || { bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
                                        return (
                                          <span style={{ background: st.bg, color: st.color, border: `1px solid ${st.border}`, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 600 }}>
                                            {followUpStatusMap[key]}
                                          </span>
                                        );
                                      })()}
                                    </div>
                                  )}

                                  {/* Done column: what was actually sold matters more
                                      than the next follow-up date. */}
                                  {col.key === "closed_won" && item.subscription && (
                                    <div style={{ paddingTop: 10, marginBottom: 4, borderTop: "1px solid #f3f4f6", display: "flex", flexDirection: "column", gap: 3 }}>
                                      <div style={{ fontSize: 12, fontWeight: 700, color: "#111827" }}>
                                        {item.subscription.plan_name || "—"}
                                        <span style={{ marginLeft: 6, color: "#16a34a" }}>
                                          ₹{Number(item.subscription.paid_amount ?? item.subscription.plan_price ?? 0).toLocaleString("en-IN")}
                                        </span>
                                      </div>
                                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                                        {item.subscription.start_date ? moment(item.subscription.start_date).format("DD MMM YYYY") : "—"}
                                        {" → "}
                                        {item.subscription.end_date ? moment(item.subscription.end_date).format("DD MMM YYYY") : "—"}
                                      </div>
                                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                        {item.subscription.plan_type === "free_trial" && (
                                          <span style={{ background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700 }}>Trial</span>
                                        )}
                                        {item.subscription.payment_status &&
                                          String(item.subscription.payment_status).toLowerCase() !== "trial" && (
                                          <span style={{ background: "#f5f5f5", color: "#4b5563", borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize" }}>
                                            {item.subscription.payment_status}
                                          </span>
                                        )}
                                        {item.subscription.subscription_status && (
                                          <span
                                            style={{
                                              borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700, textTransform: "capitalize",
                                              background: item.subscription.subscription_status === "active" ? "#f0fdf4" : "#fef2f2",
                                              color: item.subscription.subscription_status === "active" ? "#16a34a" : "#dc2626",
                                              border: "1px solid " + (item.subscription.subscription_status === "active" ? "#bbf7d0" : "#fecaca"),
                                            }}
                                          >
                                            {item.subscription.subscription_status}
                                          </span>
                                        )}
                                      </div>
                                      {item.subscription.order_id && (
                                        <div style={{ fontSize: 10, color: "#9ca3af" }}>{item.subscription.order_id}</div>
                                      )}
                                    </div>
                                  )}

                                  <div style={{ display: "flex", gap: 6, paddingTop: 10, borderTop: "1px solid #f3f4f6" }}>
                                    {parsePhones(item.additional_phone).length > 0 ? (
                                      <Dropdown
                                        trigger={["click"]}
                                        menu={{
                                          items: [
                                            { key: "primary", label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", color: "#16a34a" }}><PhoneOutlined style={{ fontSize: "11px" }} /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>Primary</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{withCountryCode(item.country_code, item.phone)}</div></div></div>) },
                                            ...parsePhones(item.additional_phone).map((alt, i, arr) => ({ key: `alt-${alt}`, label: (<div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "2px 0" }}><div style={{ width: "24px", height: "24px", borderRadius: "6px", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}><PhoneOutlined style={{ fontSize: "11px" }} /></div><div><div style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>{arr.length > 1 ? `Alternate ${i + 1}` : "Alternate"}</div><div style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{alt}</div></div></div>) })),
                                          ],
                                          onClick: ({ key }) => {
                                            const num = key === "primary" ? item.phone : key.replace(/^alt-/, "");
                                            handleClickToCall(dialNumber(item.country_code, num), item.name).then((placed) => {
                                              if (!placed) return;   // dialer refused — nothing to log
                                              setCallOutcome("Connected"); setCallNote(""); setCallDurationME(""); setCallFuStatusME(""); setCallNextDateME(""); setCallNextTimeME("");
                                              setCallLogModal({ phone: num, name: item.name, id: item.id });
                                            });
                                          },
                                        }}
                                        style={{ flex: 1 }}
                                      >
                                        <button style={{ flex: 1, width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "6px 0", border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, color: "#d97706", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}><PhoneOutlined /> Phone</button>
                                      </Dropdown>
                                    ) : (
                                      <button onClick={() => { handleClickToCall(dialNumber(item.country_code, item.phone), item.name).then((placed) => { if (!placed) return; setCallOutcome("Connected"); setCallNote(""); setCallDurationME(""); setCallFuStatusME(""); setCallNextDateME(""); setCallNextTimeME(""); setCallLogModal({ phone: item.phone, name: item.name, id: item.id }); }); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "6px 0", border: "1px solid #fde68a", background: "#fffbeb", borderRadius: 8, color: "#d97706", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}><PhoneOutlined /> Phone</button>
                                    )}
                                    <button onClick={() => { resetEmailFormME(); setEmailToME(item.email || ""); setEmailModal({ id: item.id, name: item.name, email: item.email }); }} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "6px 0", border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: 8, color: "#2563eb", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}><MailOutlined /> Email</button>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      );


                      // Order: New → Contacted → Future → Done → Negative
                      return cols.map(renderCol);
                    })()}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ALLOCATE MODAL */}
      {showAllocateModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,.55)", backdropFilter: "blur(5px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }} onClick={(e) => { if (e.target === e.currentTarget) setShowAllocateModal(false); }}>
          <div style={{ background: "white", borderRadius: "18px", width: "100%", maxWidth: "580px", overflow: "hidden", boxShadow: "0 20px 60px rgba(139,92,246,.25)", animation: "meAllocModalIn .28s ease", border: "2px solid #ede9fe" }}>
            <div style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "34px", height: "34px", background: "rgba(255,255,255,.2)", borderRadius: "9px", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "16px" }}><TeamOutlined /></div>
                <div><div style={{ color: "white", fontWeight: 700, fontSize: "15px" }}>Allocate Leads</div><div style={{ color: "rgba(255,255,255,.75)", fontSize: "11px" }}>Select an internal user to assign leads to</div></div>
              </div>
              <button onClick={() => setShowAllocateModal(false)} style={{ width: "28px", height: "28px", borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={{ padding: "20px 22px", maxHeight: "60vh", overflowY: "auto" }}>
              {internalUsers.length === 0 ? (<div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}><TeamOutlined style={{ fontSize: "40px", marginBottom: "10px", display: "block" }} /><div>No internal users found</div></div>) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {internalUsers.map((user) => {
                    const used = user.total_assigned_leads ?? 0;
                    const limit = user.lead_limit ?? 0;
                    const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                    const overLimit = limit > 0 && used >= limit;
                    return (
                      <div key={user.id} className={`alloc-admin-card${overLimit ? " over-limit" : ""}`} onClick={() => { if (!overLimit) handleAdminSelected(user); }} title={overLimit ? `${user.name} has reached their lead limit (${limit})` : ""}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "13px", fontWeight: 700, flexShrink: 0 }}>{user.name?.split(" ").map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?"}</div>
                            <div><div style={{ fontWeight: 700, fontSize: "14px", color: "#1f2937" }}>{user.name}</div><div style={{ fontSize: "12px", color: "#6b7280" }}>{user.email}</div></div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span style={{ background: overLimit ? "#fef2f2" : "#f5f3ff", color: overLimit ? "#dc2626" : "#7c3aed", border: `1px solid ${overLimit ? "#fecaca" : "#ddd6fe"}`, borderRadius: "20px", padding: "3px 10px", fontSize: "12px", fontWeight: 700 }}>{used} / {limit} leads</span>
                            {overLimit && (<div style={{ fontSize: "11px", color: "#dc2626", marginTop: "3px", fontWeight: 600 }}>Limit reached</div>)}
                          </div>
                        </div>
                        <div style={{ background: "#f3f4f6", borderRadius: "99px", height: "6px", overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: "99px", width: `${pct}%`, background: pct >= 100 ? "linear-gradient(90deg,#ef4444,#dc2626)" : pct >= 80 ? "linear-gradient(90deg,#f59e0b,#d97706)" : "linear-gradient(90deg,#8b5cf6,#7c3aed)", transition: "width .4s ease" }} />
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: "4px", fontSize: "11px", color: "#9ca3af" }}><span>Role: <strong>{user.role}</strong></span><span>{pct}% used &nbsp;·&nbsp; {Math.max(0, limit - used)} remaining</span></div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div style={{ padding: "14px 22px", borderTop: "1px solid #f0f0f0", background: "#fafafa", display: "flex", justifyContent: "flex-end" }}>
              <button onClick={() => setShowAllocateModal(false)} style={{ padding: "8px 22px", background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* CALL LOG MODAL */}
      {callLogModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(15,23,42,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "15px", width: "100%", maxWidth: "600px", maxHeight: "90vh", border: "2px solid #fde68a", overflow: "hidden", boxShadow: "0 5px 24px rgba(245,158,11,.1)", animation: "meCallModalIn .25s ease", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)", padding: "11px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                <div style={{ width: "30px", height: "30px", background: "rgba(255,255,255,.2)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}><PhoneOutlined /></div>
                <div><span style={{ color: "white", fontWeight: 700, fontSize: "14px" }}>Log a Call</span><div style={{ color: "rgba(255,255,255,.75)", fontSize: "11px", marginTop: "1px" }}>{callLogModal.name} ({callLogModal.phone})</div></div>
              </div>
              <button onClick={() => setCallLogModal(null)} style={{ width: "26px", height: "26px", borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "white", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={{ padding: "16px 18px", flex: 1, overflowY: "auto", minHeight: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "11px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "11px" }}>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Call Outcome</label>
                    <select value={callOutcome} onChange={(e) => setCallOutcome(e.target.value)} style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #fde68a", borderRadius: "9px", fontSize: "13px", outline: "none", background: "white", cursor: "pointer", color: "#374151", boxSizing: "border-box" }}>
                      {["Connected","No Answer","Voicemail Left","Call Back Later","Wrong Number"].map((o) => (<option key={o} value={o}>{o}</option>))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Call Duration</label>
                    <input value={callDurationME} onChange={(e) => setCallDurationME(e.target.value)} placeholder="e.g. 15 mins" style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #fde68a", borderRadius: "9px", fontSize: "13px", outline: "none", background: "white", boxSizing: "border-box", color: "#374151" }} />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Call Notes</label>
                  <textarea placeholder="Add notes about this call..." value={callNote} onChange={(e) => setCallNote(e.target.value)} style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #fde68a", borderRadius: "9px", fontSize: "13px", outline: "none", resize: "vertical", minHeight: "80px", background: "white", fontFamily: "inherit", boxSizing: "border-box", lineHeight: 1.6, color: "#374151", transition: "border-color .2s,box-shadow .2s" }} onFocus={(e) => { e.target.style.borderColor = "#f59e0b"; e.target.style.boxShadow = "0 0 0 3px rgba(245,158,11,.1)"; }} onBlur={(e) => { e.target.style.borderColor = "#fde68a"; e.target.style.boxShadow = "none"; }} />
                </div>
                <div>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Follow-up Status</label>
                  <FollowUpStatusSelectME value={callFuStatusME} onChange={setCallFuStatusME} followUpOptionsList={selectableFollowUpOptions} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "11px", marginTop: "4px", paddingTop: "10px", borderTop: "1px dashed #fde68a" }}>
                  <div>
                    <label style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", display: "flex", alignItems: "center", gap: "3px", marginBottom: "3px", textTransform: "uppercase", letterSpacing: "0.4px" }}><CalendarOutlined style={{ fontSize: "11px" }} /> Next Follow-up</label>
                    <input type="date" value={callNextDateME} onChange={(e) => setCallNextDateME(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: "7px", fontSize: "12px", outline: "none", background: "white", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <label style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", display: "flex", alignItems: "center", gap: "3px", marginBottom: "3px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Time</label>
                    <input type="time" value={callNextTimeME} onChange={(e) => setCallNextTimeME(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: "7px", fontSize: "12px", outline: "none", background: "white", boxSizing: "border-box" }} />
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "6px" }}>
                  <button className="me-cl-btn" onClick={() => { setCallLogModal(null); setCallNote(""); setCallOutcome("Connected"); setCallDurationME(""); setCallFuStatusME(""); setCallNextDateME(""); setCallNextTimeME(""); }} style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 18px", background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", fontWeight: 600 }}>Cancel</button>
                  <button className="me-cl-btn" disabled={addFollowUpME.isPending} onClick={() => {
                    const nowISO = moment().format("YYYY-MM-DD HH:mm:ss");
                    const userId = Number(localStorage.getItem("cms_user_id")) || 1;
                    const payload = { lead_id: callLogModal.id, followup_type: "call", followup_date: nowISO, enquiry_status: callFuStatusME, direction: "outbound", delivery_status: "delivered", call_outcome: callOutcome, call_duration: callDurationME || null, content: callNote || `Call: ${callOutcome}`, remarks: callNote || null, subject: `Call — ${callOutcome}`, recipient: callLogModal.phone, created_by: userId, ...(callNextDateME ? { next_followup_date: callNextDateME, ...(callNextTimeME ? { next_followup_time: callNextTimeME } : {}) } : {}) };
                    addFollowUpME.mutate(payload, { onSuccess: () => { queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "lead-followups" && String(q.queryKey[1]) === String(callLogModal.id) }); setCallLogModal(null); setCallNote(""); setCallOutcome("Connected"); setCallDurationME(""); setCallFuStatusME(""); setCallNextDateME(""); setCallNextTimeME(""); } });
                  }} style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 18px", background: "linear-gradient(135deg,#f59e0b,#d97706)", color: "white", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 600, boxShadow: "0 4px 12px rgba(245,158,11,.3)", opacity: addFollowUpME.isPending ? 0.7 : 1, cursor: addFollowUpME.isPending ? "not-allowed" : "pointer" }}>
                    {addFollowUpME.isPending ? (<span className="spinner-border spinner-border-sm text-white" />) : (<><CheckCircleOutlined style={{ fontSize: "12px" }} /> Log Call</>)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EMAIL COMPOSE MODAL */}
      {showPreviewME && selectedTemplateME && (<EmailPreviewModalME subject={selectedTemplateME.subject} body={selectedTemplateME.body} onClose={() => setShowPreviewME(false)} />)}
      <input type="file" multiple ref={emailFileRefME} style={{ display: "none" }} onChange={(e) => { if (!e.target.files?.length) return;
         const newFiles = Array.from(e.target.files);
         setEmailFilesME((p) => [...p, ...newFiles]); if (emailFileRefME.current) emailFileRefME.current.value = ""; }} />
      {emailModal && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: "rgba(15,23,42,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
          <div style={{ background: "white", borderRadius: "15px", width: "100%", maxWidth: "600px", height: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(102,126,234,0.22)", border: "2px solid #c7d2fe", animation: "meCallModalIn .25s ease", overflow: "hidden" }}>
            <div style={{ background: "linear-gradient(135deg,#667eea,#764ba2)", padding: "11px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
                <div style={{ width: "30px", height: "30px", background: "rgba(255,255,255,.2)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", color: "white" }}><MailOutlined /></div>
                <div><span style={{ color: "white", fontWeight: 700, fontSize: "14px" }}>Compose Mail</span><div style={{ color: "rgba(255,255,255,.75)", fontSize: "11px", marginTop: "1px" }}>{emailModal.name}{emailModal.email ? ` (${emailModal.email})` : ""}</div></div>
              </div>
              <button onClick={() => { setEmailModal(null); resetEmailFormME(); }} style={{ width: "27px", height: "27px", borderRadius: "50%", background: "rgba(255,255,255,.2)", border: "none", color: "white", cursor: "pointer", fontSize: "15px", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </div>
            <div ref={composeScrollRefME} style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 18px", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ borderRadius: "9px", border: "1.5px solid #e2e8f0", background: "white" }}>
                <div style={{ display: "flex", alignItems: "center", minHeight: "42px" }}>
                  <span style={{ padding: "0 12px", fontSize: "13px", fontWeight: 600, color: "#6b7280", minWidth: "48px", flexShrink: 0, borderRight: "1px solid #f0f0f0", alignSelf: "stretch", display: "flex", alignItems: "center" }}>To</span>
                  <input style={{ flex: 1, padding: "9px 12px", border: "none", outline: "none", fontSize: "13px", background: "transparent", minWidth: 0 }} value={emailToME} onChange={(e) => setEmailToME(e.target.value)} placeholder="recipient@email.com" />
                  <div style={{ display: "flex", gap: "2px", padding: "0 10px", flexShrink: 0 }}>
                    {!showEmailCcME && (<button type="button" onClick={() => setShowEmailCcME(true)} style={{ background: "none", border: "1px solid #e2e8f0", fontSize: "11px", fontWeight: 700, color: "#6b7280", cursor: "pointer", padding: "2px 7px", borderRadius: "4px", lineHeight: "18px" }}>Cc</button>)}
                    {!showEmailBccME && (<button type="button" onClick={() => setShowEmailBccME(true)} style={{ background: "none", border: "1px solid #e2e8f0", fontSize: "11px", fontWeight: 700, color: "#6b7280", cursor: "pointer", padding: "2px 7px", borderRadius: "4px", lineHeight: "18px" }}>Bcc</button>)}
                  </div>
                </div>
                {showEmailCcME && (
                  <div style={{ borderTop: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", minHeight: "38px" }}>
                      <span style={{ padding: "9px 12px", fontSize: "13px", fontWeight: 600, color: "#6b7280", minWidth: "48px", flexShrink: 0, borderRight: "1px solid #f0f0f0" }}>Cc</span>
                      <div style={{ flex: 1, padding: "5px 8px", display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center", minWidth: 0 }}>
                        {emailCcListME.map((e, i) => (<span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "2px 8px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: "999px", fontSize: "12px", color: "#2563eb", fontWeight: 500, flexShrink: 0 }}>{e}<button type="button" onClick={() => setEmailCcListME((p) => p.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#93c5fd", fontSize: "15px", lineHeight: 1, padding: "0 0 0 2px", display: "flex", alignItems: "center" }}>×</button></span>))}
                        <input style={{ flex: 1, minWidth: "120px", padding: "4px 2px", border: "none", outline: "none", fontSize: "13px", background: "transparent" }} value={emailCcInputME} onChange={(e) => { setEmailCcInputME(e.target.value); setEmailCcErrorME(""); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); handleAddCcEmailME(); } }} onBlur={handleAddCcEmailME} placeholder={emailCcListME.length === 0 ? "cc@email.com, press Enter to add" : ""} />
                      </div>
                    </div>
                    {emailCcErrorME && <div style={{ padding: "0 12px 5px 60px", fontSize: "11px", color: "#ef4444" }}>{emailCcErrorME}</div>}
                  </div>
                )}
                {showEmailBccME && (
                  <div style={{ borderTop: "1px solid #f0f0f0" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", minHeight: "38px" }}>
                      <span style={{ padding: "9px 12px", fontSize: "13px", fontWeight: 600, color: "#6b7280", minWidth: "48px", flexShrink: 0, borderRight: "1px solid #f0f0f0" }}>Bcc</span>
                      <div style={{ flex: 1, padding: "5px 8px", display: "flex", flexWrap: "wrap", gap: "4px", alignItems: "center", minWidth: 0 }}>
                        {emailBccListME.map((e, i) => (<span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "3px", padding: "2px 8px", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: "999px", fontSize: "12px", color: "#7c3aed", fontWeight: 500, flexShrink: 0 }}>{e}<button type="button" onClick={() => setEmailBccListME((p) => p.filter((_, j) => j !== i))} style={{ background: "none", border: "none", cursor: "pointer", color: "#c4b5fd", fontSize: "15px", lineHeight: 1, padding: "0 0 0 2px", display: "flex", alignItems: "center" }}>×</button></span>))}
                        <input style={{ flex: 1, minWidth: "120px", padding: "4px 2px", border: "none", outline: "none", fontSize: "13px", background: "transparent" }} value={emailBccInputME} onChange={(e) => { setEmailBccInputME(e.target.value); setEmailBccErrorME(""); }} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); handleAddBccEmailME(); } }} onBlur={handleAddBccEmailME} placeholder={emailBccListME.length === 0 ? "bcc@email.com, press Enter to add" : ""} />
                      </div>
                    </div>
                    {emailBccErrorME && <div style={{ padding: "0 12px 5px 60px", fontSize: "11px", color: "#ef4444" }}>{emailBccErrorME}</div>}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Email Template</label>
                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <select value={emailTemplateME} onChange={(e) => handleTemplateChangeME(e.target.value)} style={{ flex: 1, padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "9px", fontSize: "13px", outline: "none", background: "white", cursor: "pointer", boxSizing: "border-box" }}>
                    <option value="">— No Template (Manual) —</option>
                    {templatesLoading && <option disabled>Loading templates...</option>}
                    {emailTemplates.map((t) => (<option key={t.id} value={t.id}>{t.template_name.replace(/_/g, " ")}</option>))}
                  </select>
                  {selectedTemplateME && (<Tooltip title="Preview Template"><button onClick={() => setShowPreviewME(true)} style={{ width: "34px", height: "34px", borderRadius: "8px", border: "1.5px solid #c7d2fe", background: "#f0f4ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#667eea", cursor: "pointer", flexShrink: 0, fontSize: "14px" }}><EyeOutlined /></button></Tooltip>)}
                </div>
              </div>
              <div>
                <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Subject</label>
                <input value={emailSubjectME} onChange={(e) => setEmailSubjectME(e.target.value)} placeholder="Subject..." readOnly={!!selectedTemplateME} style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "9px", fontSize: "13px", outline: "none", boxSizing: "border-box", background: selectedTemplateME ? "#f8f9ff" : "white", color: selectedTemplateME ? "#6b7280" : "#374151" }} />
              </div>
              {selectedTemplateME ? (
                <div>
                  <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Body <span style={{ color: "#9ca3af", fontWeight: 400, textTransform: "none", fontSize: "10px" }}>(from template)</span></label>
                  <div dangerouslySetInnerHTML={{ __html: emailBodyME }} style={{ padding: "12px", background: "#f8f9ff", borderRadius: "9px", border: "1.5px solid #e8ecff", fontSize: "13px", color: "#374151", lineHeight: 1.6, maxHeight: "180px", overflowY: "auto" }} />
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", letterSpacing: "0.4px", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between", textTransform: "uppercase" }}>
                    <span>Body</span>
                    <div style={{ display: "flex", gap: 2, background: "#f1f5f9", borderRadius: 6, padding: 2 }}>
                      {[
                        { mode: "compose", icon: "bx-edit",  label: "Compose" },
                        { mode: "html",    icon: "bx-code",  label: "HTML"    },
                        { mode: "preview", icon: "bx-show",  label: "Preview" },
                      ].map(({ mode, icon, label }) => (
                        <button key={mode} onClick={() => setEmailBodyModeME(mode)} style={{ padding: "3px 10px", borderRadius: 4, border: "none", background: emailBodyModeME === mode ? "#fff" : "transparent", color: emailBodyModeME === mode ? "#7c3aed" : "#64748b", fontSize: 11, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4, boxShadow: emailBodyModeME === mode ? "0 1px 3px rgba(0,0,0,0.1)" : "none", textTransform: "none" }}>
                          <i className={`bx ${icon}`} style={{ fontSize: 13 }} /> {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                    {emailBodyModeME === "compose" && (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "6px 10px", borderBottom: "1px solid #f0f0f0", background: "#fafafa", flexWrap: "wrap" }}>
                          {[
                            { icon: "bx-bold",      cmd: "bold",      title: "Bold"      },
                            { icon: "bx-italic",    cmd: "italic",    title: "Italic"    },
                            { icon: "bx-underline", cmd: "underline", title: "Underline" },
                          ].map(({ icon, cmd, title }) => (
                            <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); emailExecCmdME(cmd); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>
                              <i className={`bx ${icon}`} />
                            </button>
                          ))}
                          <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                          {[
                            { icon: "bx-list-ul", cmd: "insertUnorderedList", title: "Bullet List"   },
                            { icon: "bx-list-ol", cmd: "insertOrderedList",   title: "Numbered List" },
                          ].map(({ icon, cmd, title }) => (
                            <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); emailExecCmdME(cmd); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                              <i className={`bx ${icon}`} />
                            </button>
                          ))}
                          <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                          {[
                            { icon: "bx-align-left",   cmd: "justifyLeft",   title: "Align Left"   },
                            { icon: "bx-align-middle", cmd: "justifyCenter", title: "Align Center" },
                            { icon: "bx-align-right",  cmd: "justifyRight",  title: "Align Right"  },
                          ].map(({ icon, cmd, title }) => (
                            <button key={cmd} title={title} onMouseDown={(e) => { e.preventDefault(); emailExecCmdME(cmd); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                              <i className={`bx ${icon}`} />
                            </button>
                          ))}
                          <div style={{ width: 1, height: 18, background: "#e2e8f0", margin: "0 4px" }} />
                          <button title="Link" onMouseDown={(e) => { e.preventDefault(); const url = window.prompt("Enter URL"); if (url) emailExecCmdME("createLink", url); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                            <i className="bx bx-link" />
                          </button>
                          <button title="Undo" onMouseDown={(e) => { e.preventDefault(); emailExecCmdME("undo"); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                            <i className="bx bx-undo" />
                          </button>
                          <button title="Redo" onMouseDown={(e) => { e.preventDefault(); emailExecCmdME("redo"); }} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", borderRadius: 4, color: "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                            <i className="bx bx-redo" />
                          </button>
                        </div>
                        <div ref={emailEditorRefME} contentEditable suppressContentEditableWarning onInput={() => setEmailBodyME(emailEditorRefME.current?.innerHTML || "")} style={{ minHeight: 120, padding: "12px 14px", fontSize: 13, color: "#1a2332", outline: "none", lineHeight: 1.7, wordBreak: "break-word" }} />
                      </>
                    )}
                    {emailBodyModeME === "html" && (
                      <textarea value={emailBodyME} onChange={(e) => setEmailBodyME(e.target.value)} rows={8} placeholder="Paste or write your HTML here…" style={{ width: "100%", boxSizing: "border-box", padding: "12px", border: "none", outline: "none", resize: "vertical", fontSize: 12, fontFamily: "monospace", color: "#1a2332", lineHeight: 1.6, background: "#fafafa" }} />
                    )}
                    {emailBodyModeME === "preview" && (
                      emailBodyME ? (
                        <div style={{ maxHeight: 280, overflowY: "auto" }}>
                          <iframe srcDoc={emailBodyME} style={{ width: "100%", border: "none", display: "block", minHeight: 180 }} onLoad={(e) => { const doc = e.target.contentDocument; if (doc) e.target.style.height = doc.body.scrollHeight + 16 + "px"; }} title="email-preview-me" sandbox="allow-same-origin" />
                        </div>
                      ) : (
                        <div style={{ minHeight: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", fontSize: 13 }}>Nothing to preview</div>
                      )
                    )}
                  </div>
                </div>
              )}
              <div>
                <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Remarks</label>
                <input value={emailRemarksME} onChange={(e) => setEmailRemarksME(e.target.value)} placeholder="Internal remarks..." style={{ width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: "9px", fontSize: "13px", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <label style={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", display: "block", marginBottom: "5px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Follow-up Status</label>
                <FollowUpStatusSelectME value={emailFuStatusME} onChange={setEmailFuStatusME} followUpOptionsList={selectableFollowUpOptions} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                <div><label style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", display: "flex", alignItems: "center", gap: "3px", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.4px" }}>📅 Next Follow-up</label><input type="date" value={emailNextDateME} onChange={(e) => setEmailNextDateME(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: "7px", fontSize: "12px", outline: "none", boxSizing: "border-box" }} /></div>
                <div><label style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", display: "flex", alignItems: "center", gap: "3px", marginBottom: "4px", textTransform: "uppercase", letterSpacing: "0.4px" }}>🕐 Time</label><input type="time" value={emailNextTimeME} onChange={(e) => setEmailNextTimeME(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ width: "100%", padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: "7px", fontSize: "12px", outline: "none", boxSizing: "border-box" }} /></div>
              </div>
            </div>
            <div style={{ flexShrink: 0, borderTop: "1px solid #f0f4ff", background: "white" }}>
              {(templateAttachmentsME.length > 0 || emailFilesME.length > 0) && (
                <div style={{ padding: "10px 18px 0", display: "flex", flexDirection: "column", gap: "6px" }}>
                  {templateAttachmentsME.length > 0 && (<div><div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "5px" }}>Template Attachments</div><div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>{templateAttachmentsME.map((f, i) => <EmailFileChipME key={`tpl-${i}`} file={f} onRemove={null} />)}</div></div>)}
                  {emailFilesME.length > 0 && (<div><div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "5px" }}>Added by You</div><div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>{emailFilesME.map((f, i) => (<EmailFileChipME key={i} file={f} onRemove={() => setEmailFilesME((p) => p.filter((_, j) => j !== i))} />))}</div></div>)}
                </div>
              )}
              <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: "8px" }}>
              <button onClick={() => emailFileRefME.current?.click()} style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "7px 14px", background: "#f0f4ff", color: "#667eea", border: "1.5px solid #c7d2fe", borderRadius: "9px", fontSize: "12px", fontWeight: 600, cursor: "pointer", marginRight: "auto" }}>📎 Attach Files</button>
              <button onClick={() => { setEmailModal(null); resetEmailFormME(); }} style={{ padding: "8px 18px", background: "#f1f5f9", color: "#6b7280", border: "1.5px solid #e2e8f0", borderRadius: "10px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleSendEmail} disabled={addFollowUpME.isPending || isSendingEmailME || (!selectedTemplateME && !emailBodyME.trim())} style={{ display: "inline-flex", alignItems: "center", gap: "7px", padding: "8px 20px", background: "linear-gradient(135deg,#667eea,#764ba2)", color: "white", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: 600, boxShadow: "0 4px 12px rgba(102,126,234,0.35)", opacity: (addFollowUpME.isPending || isSendingEmailME) ? 0.7 : 1, cursor: (addFollowUpME.isPending || isSendingEmailME) ? "not-allowed" : "pointer" }}>
                {(addFollowUpME.isPending || isSendingEmailME) ? <><span className="spinner-border spinner-border-sm text-white" /> Sending...</> : <><MailOutlined style={{ fontSize: "12px" }} /> Send</>}
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Follow Up full page */}
      {followUpEnquiry && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#f1f5f9", animation: "slideInRight .28s ease" }}>
          <EnquiryFollowUp
            enquiry={followUpEnquiry}
            onClose={() => {
              setFollowUpEnquiry(null);
              setSelectedLeadId(null);
              // Same reason as the Inbox: the board is looked at again right now.
              queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
            }}
            onDeleteLead={() => { setFollowUpEnquiry(null); setSelectedLeadId(null); handleDeleteSingleRow(followUpEnquiry.id); }}
            onNegativeClosed={(remarks) => { updateStatus.mutate({ id: followUpEnquiry.id, status: 2, message: remarks || "" }, { onSuccess: () => { setFollowUpEnquiry(null); setSelectedLeadId(null); } }); }}
          />
        </div>
      )}
    </>
  );
};

export default ManageEnquiry;


